package songwritersnotebook.app.database

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import kotlinx.coroutines.isActive
import songwritersnotebook.database.SongEntity
import songwritersnotebook.database.SongVersionEntity
import songwritersnotebook.database.WaveformEntity
import songwritersnotebook.app.di.PlatformDependencyProvider
import songwritersnotebook.database.AppDatabase
import songwritersnotebook.platform.getPlatformFileSystem
import songwritersnotebook.platform.PlatformFile
import songwritersnotebook.platform.FileSystem
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.where
import dev.gitlive.firebase.storage.storage
import dev.gitlive.firebase.functions.*
import io.ktor.client.request.get
import io.ktor.client.statement.readBytes
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.async.coroutines.await
import songwritersnotebook.app.toFirebaseData
import songwritersnotebook.app.getPlatform
import songwritersnotebook.app.AppDispatchers
import songwritersnotebook.app.parseJsonToNative
import songwritersnotebook.app.callIfJs
import dev.gitlive.firebase.database.*
import songwritersnotebook.app.jsUnsafeCast
import songwritersnotebook.app.JsHttpsCallableResult
import songwritersnotebook.app.utils.WaveformResult
import songwritersnotebook.app.utils.Utils
import songwritersnotebook.app.utils.NetworkUtils
import songwritersnotebook.app.utils.AppLogger
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.*
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.SongVersion
import songwritersnotebook.app.data.VersionType
import songwritersnotebook.app.data.UserPresence
import songwritersnotebook.app.data.Invitation
import songwritersnotebook.app.data.Section
import songwritersnotebook.app.data.Line
import songwritersnotebook.app.data.Track
import songwritersnotebook.app.data.TrackItem
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.SaveSongRequest
import songwritersnotebook.app.data.SaveSongResponse
import songwritersnotebook.app.data.RecordingMetadata
import songwritersnotebook.app.data.UserProfile
import songwritersnotebook.app.data.PublicUser
import songwritersnotebook.app.data.UpdateUsernameRequest
import songwritersnotebook.app.data.UpdateUsernameResponse
import songwritersnotebook.app.data.RegisterSessionRequest
import songwritersnotebook.app.data.RemoveSessionRequest
import songwritersnotebook.app.data.FindUserRequest
import songwritersnotebook.app.data.FindUserResponse
import songwritersnotebook.app.data.SearchUsersResponse
import kotlin.math.pow

enum class UploadStatus { PENDING, UPLOADING, SUCCESS, ERROR }

class SongRepository(
    private val database: AppDatabase,
    private val dependencyProvider: PlatformDependencyProvider? = null
) {
    private val crdtSyncManager = songwritersnotebook.app.crdt.SongCrdtSyncManager()
    private var observedSongId: String? = null

    val pendingDeltasCount: StateFlow<Int> = crdtSyncManager.pendingDeltasCount
    val sessionDeviceId: String get() = crdtSyncManager.sessionDeviceId

    fun getBakeStatus(songId: String): Flow<String?> {
        return dev.gitlive.firebase.Firebase.database.reference("songs/$songId/bakeStatus").valueEvents
            .map { 
                try { it.value<String?>() } catch(e: Exception) { null }
            }
            .catch { e ->
                AppLogger.d("SongRepository", "Error listening to bakeStatus for $songId: $e")
                emit(null) 
            }
    }

    // --- User Profile & Search ---

    private val _currentUserProfile = MutableStateFlow<UserProfile?>(null)
    val currentUserProfile = _currentUserProfile.asStateFlow()

    fun pushCrdtUpdate(
        songId: String, 
        song: Song, 
        scope: songwritersnotebook.app.crdt.UndoScope, 
        forceUndoStep: Boolean = false, 
        isUndoable: Boolean = true,
        shouldPushToCloud: Boolean = true
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val isOwner = song.ownerId == userId
        val isCollaborator = song.collaborators.contains(userId)
        val isViewer = song.viewers.contains(userId)

        val allowed = when {
            isOwner -> {
                val currentProfile = _currentUserProfile.value
                val tier = currentProfile?.subscriptionTier ?: "FREE"
                val isShared = song.collaborators.isNotEmpty() || song.viewers.isNotEmpty() || song.collaboratorEmails.isNotEmpty()
                // Owners must be STUDIO or have shared the song to sync. 
                // We allow if profile is still loading (optimistic).
                (currentProfile == null || tier == "STUDIO" || isShared) && song.ownerId.isNotEmpty()
            }
            isCollaborator -> true // Editors always allowed to sync
            isViewer -> false     // Viewers blocked
            else -> false
        }
        
        // We always push real-time deltas to RTDB regardless of presence size to guarantee CRDT integrity.
        // Relying on presence size is flawed because of network desyncs, Firebase Rules filtering,
        // or one device joining slightly later.
        val finalShouldSaveToCloud = shouldPushToCloud && allowed

        crdtSyncManager.pushUpdate(song, songId, userId, scope, finalShouldSaveToCloud, forceUndoStep = forceUndoStep, isUndoable = isUndoable)
    }

    suspend fun pushCrdtUpdateSuspend(
        songId: String, 
        song: Song, 
        scope: songwritersnotebook.app.crdt.UndoScope, 
        forceUndoStep: Boolean = false, 
        isUndoable: Boolean = true,
        shouldPushToCloud: Boolean = true
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val isOwner = song.ownerId == userId
        val isCollaborator = song.collaborators.contains(userId)
        val isViewer = song.viewers.contains(userId)

        val allowed = when {
            isOwner -> {
                val currentProfile = _currentUserProfile.value
                val tier = currentProfile?.subscriptionTier ?: "FREE"
                val isShared = song.collaborators.isNotEmpty() || song.viewers.isNotEmpty() || song.collaboratorEmails.isNotEmpty()
                (currentProfile == null || tier == "STUDIO" || isShared) && song.ownerId.isNotEmpty()
            }
            isCollaborator -> true
            isViewer -> false
            else -> false
        }
        
        val finalShouldSaveToCloud = shouldPushToCloud && allowed

        crdtSyncManager.pushUpdateSuspend(song, songId, userId, scope, finalShouldSaveToCloud, forceUndoStep = forceUndoStep, isUndoable = isUndoable)
    }

    // --- PRO COLLABORATION: Atomic Splicing ---

    fun pushLineTextUpdate(
        songId: String,
        sectionId: String,
        lineId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        isShared: Boolean
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val tier = _currentUserProfile.value?.subscriptionTier ?: "FREE"
        
        // Optimistic check: if it's shared or the user is Studio, allow cloud push.
        val allowedByTier = (tier == "STUDIO" || isShared)
        
        crdtSyncManager.pushTextUpdate(songId, userId, sectionId, lineId, index, deleteCount, insertText, allowedByTier)
    }

    fun pushSectionTitleUpdate(
        songId: String,
        userId: String,
        sectionId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        isShared: Boolean
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val tier = _currentUserProfile.value?.subscriptionTier ?: "FREE"
        val allowedByTier = (tier == "STUDIO" || isShared)
        
        crdtSyncManager.pushSectionTitleUpdate(songId, userId, sectionId, index, deleteCount, insertText, allowedByTier)
    }

    fun pushSongTitleUpdate(
        songId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        isShared: Boolean
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val tier = _currentUserProfile.value?.subscriptionTier ?: "FREE"
        val allowedByTier = (tier == "STUDIO" || isShared)
        
        crdtSyncManager.pushSongTitleUpdate(songId, userId, index, deleteCount, insertText, allowedByTier)
    }

    fun pushSongNotesUpdate(
        songId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        isShared: Boolean
    ) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        val tier = _currentUserProfile.value?.subscriptionTier ?: "FREE"
        val allowedByTier = (tier == "STUDIO" || isShared)
        
        crdtSyncManager.pushSongNotesUpdate(songId, userId, index, deleteCount, insertText, allowedByTier)
    }

    fun saveLineDirect(songId: String, line: Line) {
        // In the new CRDT-centric model, "direct" saves should still flow through the CRDT
        // to ensure all collaborators see the change instantly.
        repositoryScope.launch {
            val song = getSongById(songId)
            if (song != null) {
                val updatedSections = song.sections.map { section ->
                    if (section.id == line.sectionId) {
                        section.copy(lines = section.lines.map { if (it.id == line.id) line else it })
                    } else section
                }
                pushCrdtUpdate(songId, song.copy(sections = updatedSections), scope = songwritersnotebook.app.crdt.UndoScope.EDITOR)
            }
        }
    }

    suspend fun updatePresence(songId: String, presence: UserPresence) {
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        crdtSyncManager.updatePresence(songId, presence)
    }

    suspend fun clearPresence(songId: String) {
        crdtSyncManager.clearPresence(songId)
    }

    fun updateUserProfile(profile: UserProfile?) {
        _currentUserProfile.value = profile
    }

    suspend fun updateUserProfileFields(fields: Map<String, Any?>) {
        val userId = auth.currentUser?.uid ?: return
        try {
            val userRef = firestore.collection("users").document(userId)
            val updates = fields.map { it.key to it.value }.toTypedArray()
            userRef.update(*updates)
        } catch (e: Exception) {
            println("Error updating user profile fields: $e")
            throw e
        }
    }

    suspend fun updateUsername(newUsername: String): String {
        val functions = dev.gitlive.firebase.Firebase.functions("us-central1")
        val requestJson = mapOf("username" to newUsername)
        val result = callCloudFunction("updateUsername", requestJson)
        
        return try {
            val response = result.data<UpdateUsernameResponse>()
            response.username
        } catch (e: Exception) {
            println("Error decoding updateUsername response: $e")
            newUsername
        }
    }

    suspend fun searchUsers(query: String): List<PublicUser> {
        val functions = dev.gitlive.firebase.Firebase.functions("us-central1")
        val requestJson = mapOf("usernameQuery" to query)
        val result = callCloudFunction("searchUsers", requestJson)
        
        return try {
            val response = result.data<SearchUsersResponse>()
            response.users
        } catch (e: Exception) {
            println("Error in searchUsers decoding: $e")
            emptyList()
        }
    }

    suspend fun removeCollaborator(songId: String, collaboratorId: String) {
        val song = getSongById(songId) ?: return

        // 1. Call Cloud Function first (Source of Truth)
        try {
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"songId\":\"$songId\",\"collaboratorId\":\"$collaboratorId\"}"
                songwritersnotebook.app.callIfJs("removeCollaborator", Any(), jsData, token)
            } else {
                callCloudFunction("removeCollaborator", mapOf("songId" to songId, "collaboratorId" to collaboratorId))
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to remove collaborator via cloud: $e")
            throw Exception("Failed to remove collaborator: ${e.message}") // Rethrow to notify ViewModel
        }

        // 2. Update Local State (Optimistic)
        val nextLockedSections = song.lockedSections.filterValues { it != collaboratorId }
        val nextLockedTracks = song.lockedTracks.filterValues { it != collaboratorId }

        val updatedSong = song.copy(
            collaborators = song.collaborators - collaboratorId,
            viewers = song.viewers - collaboratorId,
            collaboratorEmails = song.collaboratorEmails.filterKeys { it != collaboratorId },
            // IMMEDIATELY RELEASE LOCKS when user is removed
            lockedSections = nextLockedSections,
            lockedSectionTimestamps = song.lockedSectionTimestamps.filterKeys { nextLockedSections.containsKey(it) },
            lockedTracks = nextLockedTracks,
            lockedTrackTimestamps = song.lockedTrackTimestamps.filterKeys { nextLockedTracks.containsKey(it) },
            hasActiveLocks = nextLockedSections.isNotEmpty() || nextLockedTracks.isNotEmpty()
        )
        
        // Save locally only, avoiding `saveSongToAllSources` to prevent full payload push
        database.songQueriesQueries.insertSong(
            id = updatedSong.id,
            ownerId = updatedSong.ownerId,
            title = updatedSong.title,
            lastModified = updatedSong.lastModified.toEpochMilliseconds(),
            bpm = updatedSong.bpm.toLong(),
            isDeleted = if (updatedSong.isDeleted) 1L else 0L,
            data_ = json.encodeToString(Song.serializer(), updatedSong)
        )
        
        // Update in-memory flow
        _songs.value = _songs.value.map { if (it.id == updatedSong.id) updatedSong else it }

        // IMMEDIATELY CLEAR REMOTE PRESENCE
        crdtSyncManager.clearAllPresenceForUser(songId, collaboratorId)
    }
    
    suspend fun toggleCollaboratorRole(songId: String, collaboratorId: String) {
        val song = getSongById(songId) ?: return
        val wasEditor = song.collaborators.contains(collaboratorId)

        // 1. Call Cloud Function first (Source of Truth)
        try {
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"songId\":\"$songId\",\"collaboratorId\":\"$collaboratorId\"}"
                songwritersnotebook.app.callIfJs("toggleCollaboratorRole", Any(), jsData, token)
            } else {
                callCloudFunction("toggleCollaboratorRole", mapOf("songId" to songId, "collaboratorId" to collaboratorId))
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to toggle role via cloud: $e")
            return
        }
        
        // 2. Update Local State (Optimistic)
        val updatedSong = if (wasEditor) {
            // Demote to Viewer
            val nextLockedSections = song.lockedSections.filterValues { it != collaboratorId }
            val nextLockedTracks = song.lockedTracks.filterValues { it != collaboratorId }

            song.copy(
                collaborators = song.collaborators - collaboratorId,
                viewers = song.viewers + collaboratorId,
                // RELEASE LOCKS if demoting to viewer
                lockedSections = nextLockedSections,
                lockedSectionTimestamps = song.lockedSectionTimestamps.filterKeys { nextLockedSections.containsKey(it) },
                lockedTracks = nextLockedTracks,
                lockedTrackTimestamps = song.lockedTrackTimestamps.filterKeys { nextLockedTracks.containsKey(it) },
                hasActiveLocks = nextLockedSections.isNotEmpty() || nextLockedTracks.isNotEmpty()
            )
        } else {
            // Promote to Editor
            song.copy(
                collaborators = song.collaborators + collaboratorId,
                viewers = song.viewers - collaboratorId
            )
        }
        
        // Save locally only
        database.songQueriesQueries.insertSong(
            id = updatedSong.id,
            ownerId = updatedSong.ownerId,
            title = updatedSong.title,
            lastModified = updatedSong.lastModified.toEpochMilliseconds(),
            bpm = updatedSong.bpm.toLong(),
            isDeleted = if (updatedSong.isDeleted) 1L else 0L,
            data_ = json.encodeToString(Song.serializer(), updatedSong)
        )
        
        // Update in-memory flow
        _songs.value = _songs.value.map { if (it.id == updatedSong.id) updatedSong else it }
    }

    suspend fun blockUser(targetUserId: String, targetEmail: String) {
        val currentProfile = _currentUserProfile.value ?: return
        val updatedBlocked = currentProfile.blockedUsers.toMutableMap()
        updatedBlocked[targetUserId] = targetEmail
        
        updateUserProfileFields(mapOf("blockedUsers" to updatedBlocked))
        _currentUserProfile.value = currentProfile.copy(blockedUsers = updatedBlocked)

        // GLOBAL CLEANUP: 
        // 1. Remove blocked user from all songs I OWN
        removeAllCollaborationsForUser(targetUserId)
        
        // 2. Leave all songs owned by THEM
        leaveAllCollaborationsFromUser(targetUserId)

        // LOCAL CLEANUP: Immediate UI update for currently loaded songs
        val userId = auth.currentUser?.uid ?: return
        val ownedSongs = (_songs.value).filter { it.ownerId == userId }
        ownedSongs.forEach { song ->
            if (song.collaborators.contains(targetUserId) || song.viewers.contains(targetUserId)) {
                // removeCollaborator handles local DB and RTDB presence cleanup
                removeCollaborator(song.id, targetUserId)
            }
        }
    }

    suspend fun getCurrentUserProfile(): UserProfile? {
        val userId = auth.currentUser?.uid ?: return null
        try {
            val doc = firestore.collection("users").document(userId).get()
            if (doc.exists) {
                val profile = doc.data<UserProfile>()
                profile.uid = userId
                _currentUserProfile.value = profile
                return profile
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Error fetching user profile (keeping last known): $e")
            // CRITICAL: Do NOT set _currentUserProfile to null here. 
            // Keeping the last known profile prevents the app from "kicking" the user to home.
        }
        return _currentUserProfile.value
    }

    private val fileSystem = getPlatformFileSystem()
    private val appFilesDir = fileSystem.getAppFilesDirectory()

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        isLenient = true
        allowSpecialFloatingPointValues = true
        explicitNulls = false
    }
    
    private val firestore = Firebase.firestore
    private val storage = Firebase.storage
    private val auth = Firebase.auth
    
    val repositoryScope = CoroutineScope(songwritersnotebook.app.AppDispatchers.IO + SupervisorJob())

    private val networkUtils = songwritersnotebook.app.utils.NetworkUtils()

    private val _syncChannel = kotlinx.coroutines.channels.Channel<Unit>(kotlinx.coroutines.channels.Channel.CONFLATED)

    private fun triggerSync() {
        _syncChannel.trySend(Unit)
    }

    init {
        repositoryScope.launch {
            startSyncEngine()
        }
        repositoryScope.launch {
            networkUtils.networkStatus.collect { isAvailable: Boolean ->
                if (isAvailable) {
                    AppLogger.d("SongRepository", "Network restored. Triggering sync engine.")
                    triggerSync()
                }
            }
        }
    }

    private suspend fun startSyncEngine() {
        var backoffMs = 2000L
        val maxBackoffMs = 60_000L
        
        while (currentCoroutineContext().isActive) {
            // Wait for a trigger
            _syncChannel.receive()

            // Wait for debounce to coalesce rapid changes
            delay(5000)

            // Clear any pending triggers that accumulated during the debounce
            while (_syncChannel.tryReceive().isSuccess) {}

            if (!networkUtils.isNetworkAvailable()) {
                AppLogger.d("SongRepository", "SyncEngine: Offline. Suspending until network is restored.")
                continue
            }

            var allSuccess = true
            
            try {
                val userId = auth.currentUser?.uid ?: continue
                val songsToSync = database.songQueriesQueries.getAllSongs().awaitAsList().mapNotNull { songEntity ->
                    try { json.decodeFromString<Song>(songEntity.data_) } catch(e: Exception) { null }
                }.filter { song ->
                    val lastSyncedMs = song.lastSynced?.toEpochMilliseconds() ?: 0L
                    val lastModifiedMs = song.lastModified.toEpochMilliseconds()
                    // Sync if it was never synced, if there's a sync error, or if modified after sync
                    (lastSyncedMs == 0L || lastModifiedMs > lastSyncedMs || song.isSyncError) && 
                    (song.ownerId == userId || song.collaborators.contains(userId))
                }

                if (songsToSync.isEmpty()) {
                    backoffMs = 2000L // Reset backoff
                    continue
                }

                AppLogger.d("SongRepository", "SyncEngine: Found ${songsToSync.size} pending songs.")

                for (song in songsToSync) {
                    val currentProfile = _currentUserProfile.value
                    val tier = currentProfile?.subscriptionTier ?: "FREE"
                    val isOwner = song.ownerId == userId
                    val isShared = song.collaborators.isNotEmpty() || song.viewers.isNotEmpty() || song.collaboratorEmails.isNotEmpty()
                    val isCollaborator = song.collaborators.contains(userId)
                    
                    val shouldSaveToCloud = when {
                        isOwner -> (currentProfile == null || tier == "STUDIO" || isShared || song.isDeleted) && song.ownerId.isNotEmpty()
                        isCollaborator -> true
                        else -> false
                    }

                    if (!shouldSaveToCloud) continue

                    // PRO OPTIMIZATION: Skip background sync for ANY active CRDT session.
                    // If the song is currently open in the Editor, the CRDT Baker cloud function 
                    // handles all content saves automatically. We don't want SyncEngine fighting it
                    // or spamming the saveSong endpoint on every keystroke.
                    val isActiveInEditor = crdtSyncManager.activeSongId == song.id
                    if (isActiveInEditor) {
                        AppLogger.d("SongRepository", "SyncEngine: Skipping '${song.title}' because it is in an active multiplayer session.")
                        continue
                    }

                    AppLogger.d("SongRepository", "SyncEngine: Attempting to sync '${song.title}'")

                    val request = SaveSongRequest(
                        songId = song.id,
                        revisionId = song.revisionId,
                        lastModified = song.lastModified.toEpochMilliseconds(),
                        song = song,
                        isDeleted = if (song.isDeleted) true else null,
                        crdtState = song.crdtState
                    )

                    val result = try {
                        if (isJs) {
                            val user = auth.currentUser
                            val isWeb = getPlatform().name.contains("Web", ignoreCase = true) || getPlatform().name.contains("JS", ignoreCase = true)
                            val jsResult = if (user != null) {
                                val token = user.getIdToken(false)
                                if (isWeb) {
                                    val jsonString = kotlinx.serialization.json.Json.encodeToString(songwritersnotebook.app.data.SaveSongRequest.serializer(), request)
                                    songwritersnotebook.app.callIfJs("saveSong", Any(), jsonString, token)
                                } else {
                                    songwritersnotebook.app.callIfJs("saveSong", Any(), request, token)
                                }
                            } else {
                                if (isWeb) {
                                    val jsonString = kotlinx.serialization.json.Json.encodeToString(songwritersnotebook.app.data.SaveSongRequest.serializer(), request)
                                    songwritersnotebook.app.callIfJs("saveSong", Any(), jsonString, null)
                                } else {
                                    songwritersnotebook.app.callIfJs("saveSong", Any(), request, null)
                                }
                            }
                            jsResult.jsUnsafeCast<dev.gitlive.firebase.functions.HttpsCallableResult>()
                        } else {
                            AppLogger.d("FIRESTORE_USAGE", "WRITE: Calling saveSong Cloud Function for ${request.songId}")
                            callCloudFunction("saveSong", request)
                        }
                    } catch (e: Exception) {
                        AppLogger.d("SongRepository", "SyncEngine: Cloud save failed for ${song.title} with exception: ${e.message}")
                        allSuccess = false
                        if (e.message?.contains("permission-denied", ignoreCase = true) == true || e.message?.contains("permission", ignoreCase = true) == true) {
                            // If we lost permission, delete the song locally so it stops trying to sync
                            AppLogger.d("SongRepository", "SyncEngine: Permission permanently denied for ${song.title}. Removing locally.")
                            database.songQueriesQueries.deleteSong(song.id)
                        } else if (e.message?.contains("too large", ignoreCase = true) == true || e.message?.contains("413", ignoreCase = true) == true) {
                             AppLogger.d("SongRepository", "SyncEngine: Payload too large for ${song.title}. Flagging as error.")
                             saveSong(song.copy(isSyncError = true))
                        } else {
                            // Transient error (e.g. timeout, network offline). Leave as Sync Pending.
                            AppLogger.d("SongRepository", "SyncEngine: Transient network error for ${song.title}. Retrying later.")
                        }
                        continue
                    }

                    val response = try { 
                        if (isJs) {
                            // Bypass GitLive KMP JS parser which crashes on raw JS objects.
                            // If we reach this line, the JS fetch promise resolved successfully.
                            songwritersnotebook.app.data.SaveSongResponse(status = "success", message = "Parsed manually", revisionId = null, totalBytes = null)
                        } else {
                            result.data<songwritersnotebook.app.data.SaveSongResponse>()
                        }
                    } catch (e: Exception) {
                        songwritersnotebook.app.data.SaveSongResponse(status = "error", message = "Could not parse response: ${e.message}", revisionId = null, totalBytes = null)
                    }

                    if (response.status == "success") {
                        AppLogger.d("SongRepository", "SyncEngine: Cloud save successful for ${song.title}.")
                        val syncedSong = song.copy(
                            lastSynced = song.lastModified, 
                            isSyncError = false, 
                            revisionId = response.revisionId ?: song.revisionId
                        )
                        saveSong(syncedSong)
                        
                        if (song.ownerId != userId) {
                            val currentShared = _sharedSongs.value.toMutableList()
                            val index = currentShared.indexOfFirst { it.id == song.id }
                            if (index != -1) { 
                                currentShared[index] = syncedSong
                                _sharedSongs.value = currentShared 
                            }
                        }
                    } else {
                        AppLogger.d("SongRepository", "SyncEngine: Cloud save rejected for ${song.title}: ${response.message}")
                        handleSaveError(song, Exception(response.message))
                        allSuccess = false
                    }
                }

                if (allSuccess) {
                    backoffMs = 2000L // Reset backoff on full success
                } else {
                    AppLogger.d("SongRepository", "SyncEngine: Some saves failed. Applying backoff of ${backoffMs}ms")
                    delay(backoffMs)
                    val jitter = (0..1000).random().toLong()
                    backoffMs = kotlin.math.min(maxBackoffMs, (backoffMs * 1.5).toLong() + jitter)
                    triggerSync() // Trigger next attempt
                }

            } catch (e: Exception) {
                AppLogger.d("SongRepository", "SyncEngine: Exception during sync loop: ${e.message}")
                delay(backoffMs)
                val jitter = (0..1000).random().toLong()
                backoffMs = kotlin.math.min(maxBackoffMs, (backoffMs * 1.5).toLong() + jitter)
                triggerSync() // Trigger next attempt
            }
        }
    }

    private val _songs = MutableStateFlow<List<Song>>(emptyList())
    val songs: StateFlow<List<Song>> = _songs.asStateFlow()

    private val _sharedSongs = MutableStateFlow<List<Song>>(emptyList())
    val sharedSongs: StateFlow<List<Song>> = _sharedSongs.asStateFlow()

    private val _invitations = MutableStateFlow<List<songwritersnotebook.app.data.Invitation>>(emptyList())
    val invitations: StateFlow<List<songwritersnotebook.app.data.Invitation>> = _invitations.asStateFlow()

    private val _uploadStatus = MutableStateFlow<Map<String, UploadStatus>>(emptyMap())
    val uploadStatus: StateFlow<Map<String, UploadStatus>> = _uploadStatus.asStateFlow()

    private val isJs = getPlatform().name.contains("Web", ignoreCase = true)

    @OptIn(kotlinx.serialization.ExperimentalSerializationApi::class)
    private suspend inline fun <reified T> callCloudFunction(name: String, data: T?): dev.gitlive.firebase.functions.HttpsCallableResult {
        val callable = dev.gitlive.firebase.Firebase.functions("us-central1").httpsCallable(name)
        if (data == null) return callable.invoke()
        return callable.invoke(data)
    }

    private suspend fun callCloudFunctionNoArg(name: String): dev.gitlive.firebase.functions.HttpsCallableResult {
        return dev.gitlive.firebase.Firebase.functions("us-central1").httpsCallable(name).invoke()
    }

    init {
        repositoryScope.launch {
            combine(
                getAllSongs(), 
                auth.authStateChanged.onStart { emit(auth.currentUser) }
            ) { localSongs, user ->
                val userId = user?.uid
                
                localSongs.filter { song ->
                    val isDeleted = song.isDeleted
                    val isOwner = song.ownerId == userId
                    val isAnonymous = song.ownerId.isEmpty() || song.ownerId == "anonymous"
                    val isCollaborator = userId != null && song.collaborators.contains(userId)
                    val keep = if (isDeleted) false else if (userId == null) isAnonymous else isOwner || isAnonymous || isCollaborator
                    if (!keep) {
                        println("DEBUG_UI: Dropped song ${song.id} | title=${song.title} | isDeleted=$isDeleted | ownerId=${song.ownerId} vs userId=$userId | isOwner=$isOwner | isCollab=$isCollaborator")
                    }
                    keep
                }
            }.collect { filteredSongs ->
                _songs.value = filteredSongs
            }
        }
    }

    fun getMostRecentSongTitle(): String? {
        return _songs.value.maxByOrNull { it.lastModified }?.title
    }

    fun getCurrentUserId(): String? = auth.currentUser?.uid

    suspend fun getWaveform(url: String): WaveformResult? {
        return withContext(songwritersnotebook.app.AppDispatchers.IO) {
            val userId = auth.currentUser?.uid ?: ""
            val entity = database.appDatabaseQueries.getWaveform(url, userId).awaitAsOneOrNull() ?: return@withContext null
            val points = entity.points.split(",").filter { it.isNotEmpty() }.map { it.toFloat() }
            WaveformResult(points, entity.durationMillis)
        }
    }

    suspend fun saveWaveform(url: String, result: WaveformResult) {
        withContext(songwritersnotebook.app.AppDispatchers.IO) {
            val userId = auth.currentUser?.uid ?: ""
            val pointsStr = result.points.joinToString(",")
            database.appDatabaseQueries.insertWaveform(url, userId, pointsStr, result.durationMillis)
        }
    }

    suspend fun saveSong(song: Song) {
        val userId = auth.currentUser?.uid
        
        // PRO SYNC GUARD: Load existing local data to prevent "Shell Overwrite"
        val existingEntity = database.songQueriesQueries.getSongById(song.id, userId ?: "").awaitAsOneOrNull()
        val existingSong = existingEntity?.let {
            try { json.decodeFromString<Song>(it.data_) } catch (e: Exception) { null }
        }
        
        val wasDeleted = existingEntity?.isDeleted == 1L
        val finalIsDeleted = if (wasDeleted) true else song.isDeleted
        
        // SHELL PROTECTION: If the incoming song is a shell but we have rich local content,
        // merge the incoming metadata (title, owner, etc.) with the local content.
        val isIncomingShell = song.sections.isEmpty() && (song.studioState?.tracks?.isEmpty() ?: true)
        val hasLocalContent = existingSong != null && (existingSong.sections.isNotEmpty() || (existingSong.studioState?.tracks?.isNotEmpty() ?: false))
        
        val mergedSong = if (isIncomingShell && hasLocalContent && !finalIsDeleted) {
            AppLogger.d("SongRepository", "SHELL PROTECTION - Merging metadata into rich local content for ${song.id}")
            existingSong!!.copy(
                title = song.title.ifBlank { existingSong.title },
                ownerId = song.ownerId.ifBlank { existingSong.ownerId },
                ownerUsername = song.ownerUsername.ifBlank { existingSong.ownerUsername },
                collaborators = if (song.collaborators.isNotEmpty()) song.collaborators else existingSong.collaborators,
                viewers = if (song.viewers.isNotEmpty()) song.viewers else existingSong.viewers,
                lastModified = if (song.lastModified > existingSong.lastModified) song.lastModified else existingSong.lastModified,
                crdtState = song.crdtState ?: existingSong.crdtState,
                revisionId = song.revisionId ?: existingSong.revisionId
            )
        } else {
            song
        }
        
        val finalTitle = mergedSong.title
        val songToSave = if (wasDeleted) mergedSong.copy(isDeleted = true) else mergedSong
        
        if (mergedSong.ownerId.isNotEmpty() && mergedSong.ownerId != userId && userId != null) {
            val currentShared = _sharedSongs.value.toMutableList()
            val index = currentShared.indexOfFirst { it.id == song.id }
            if (index != -1) {
                if (finalIsDeleted) currentShared.removeAt(index)
                else currentShared[index] = songToSave
            } else if (!finalIsDeleted) {
                currentShared.add(songToSave)
            }
            _sharedSongs.value = currentShared
        }

        val songJson = json.encodeToString(songToSave.copy(presence = emptyMap(), isDirty = false))
        
        try {
            database.songQueriesQueries.insertSong(
                id = songToSave.id,
                ownerId = songToSave.ownerId,
                title = finalTitle.ifBlank { existingEntity?.title ?: "" },
                lastModified = songToSave.lastModified.toEpochMilliseconds(),
                bpm = songToSave.bpm.toLong(),
                isDeleted = if (finalIsDeleted) 1L else 0L,
                data_ = songJson
            )
            println("DEBUG_SYNC: successfully saved song to sqlite ${songToSave.id}")
        } catch (e: Throwable) {
            println("DEBUG_SYNC_ERROR: insertSong failed for ${songToSave.id}. Message: ${e.message}, Cause: ${e.cause}")
            e.printStackTrace()
        }
    }

    suspend fun getAllSongs(): Flow<List<Song>> {
        val userId = auth.currentUser?.uid ?: ""
        println("DEBUG_UI: getAllSongs called for user: $userId")
        return database.songQueriesQueries.getAllSongs()
            .asFlow()
            .mapToList(Dispatchers.Default)
            .onEach { println("DEBUG_UI: Database emitted ${it.size} entities") }
            .map { songEntities ->
                songEntities.map { songEntity ->
                    try {
                        val s = json.decodeFromString<Song>(songEntity.data_)
                        // PRO SYNC: We no longer "heal" titles here. 
                        // If the JSON says it's blank, it's blank.
                        s.copy(isDeleted = songEntity.isDeleted == 1L)
                    } catch (e: Exception) {
                        val rawJson = songEntity.data_
                        val extractedOwnerId = if (rawJson.contains("\"ownerId\":\"")) rawJson.substringAfter("\"ownerId\":\"").substringBefore("\"") else ""
                        Song(
                            id = songEntity.id,
                            title = songEntity.title,
                            bpm = songEntity.bpm.toInt(),
                            lastModified = kotlinx.datetime.Instant.fromEpochMilliseconds(songEntity.lastModified),
                            ownerId = extractedOwnerId,
                            isDeleted = songEntity.isDeleted == 1L
                        )
                    }
                }.filter { song ->
                    // Proactive Ghost Shell Filter: Hide unprompted/abandoned "New Song" instances
                    // STABILITY FIX: Never auto-delete a song that is currently being observed/edited
                    val isCurrentlyObserved = observedSongId == song.id
                    val hasStudioContent = (song.studioState?.tracks?.any { it.items.isNotEmpty() } == true)
                    val isBlankShell = !isCurrentlyObserved && 
                                       song.title == "New Song" && 
                                       song.recordings.isEmpty() && 
                                       !hasStudioContent &&
                                       song.sections.all { sec -> sec.lines.all { l -> l.text.isBlank() } }
                    
                    if (isBlankShell && !song.isDeleted) {
                        val ageMs = kotlinx.datetime.Clock.System.now().toEpochMilliseconds() - song.lastModified.toEpochMilliseconds()
                        // Garbage Collection: If Ghost abandoned over 30 Seconds, physically execute Deletion sync
                        if (ageMs > 30_000) {
                            repositoryScope.launch {
                                try { deleteSong(song.id) } catch (e: Exception) {}
                            }
                        }
                    }
                    
                    !isBlankShell
                }
            }
            .onEach { println("DEBUG_UI: After filtering, emitting ${it.size} songs") }
    }

    private suspend fun fetchFullSongFromFirestore(songId: String): Song? {
        try {
            val songDoc = firestore.collection("songs").document(songId).get()
            if (!songDoc.exists) {
                AppLogger.d("SongRepository", "fetchFullSong FAILED - Document does not exist: $songId")
                return null
            }

            // --- PRO POLICY: Metadata extraction only ---
            var crdtDocData: String? = null
            try {
                val crdtDoc = firestore.collection("songs").document(songId).collection("crdt").document("payload").get()
                if (crdtDoc.exists) crdtDocData = crdtDoc.get<String>("data")
            } catch(e: Exception) { }

            val baseSong = try {
                val s = songDoc.data<Song>().copy(id = songId)
                val finalTitle = s.title.ifBlank { try { songDoc.get<String>("title") } catch(e: Exception) { "" } ?: "" }
                s.copy(title = finalTitle, crdtState = crdtDocData ?: s.crdtState)
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Failed to deserialize base song $songId: ${e.stackTraceToString()}")
                Song(
                    id = songId,
                    title = try { songDoc.get<String>("title") } catch(e2: Exception) { "" } ?: "",
                    ownerId = try { songDoc.get<String>("ownerId") } catch(e2: Exception) { "" } ?: "",
                    collaborators = try { songDoc.get<List<String>>("collaborators") } catch(e2: Exception) { emptyList() },
                    viewers = try { songDoc.get<List<String>>("viewers") } catch(e2: Exception) { emptyList() },
                    collaboratorEmails = try { songDoc.get<Map<String, String>>("collaboratorEmails") } catch(e2: Exception) { emptyMap() },
                    crdtState = crdtDocData
                )
            }

            // --- PRO POLICY: Binary First Authority ---
            // If CRDT exists, it is the absolute truth. We ignore JSON sections/lines.
            if (baseSong.crdtState != null) {
                val crdtBytes = decodeCrdtState(baseSong.crdtState)
                if (crdtBytes != null) {
                    val reconstructed = parseSongFromCrdt(crdtBytes, baseSong)
                    if (reconstructed != null) {
                        AppLogger.d("SongRepository", "Binary-First authority for $songId. Reconstruction successful.")
                        return reconstructed
                    }
                }
                AppLogger.d("SongRepository", "Binary state present but corrupted for $songId. Falling back to Vault.")
            }

            // --- PRO POLICY: Root JSON Fallback ---
            // If the root document already contains arrays (e.g. from a prior bake or save), use it directly.
            if (baseSong.sections.isNotEmpty() || baseSong.studioState?.tracks?.isNotEmpty() == true) {
                AppLogger.d("SongRepository", "Root document contains rich JSON content for $songId. Returning baseSong.")
                return baseSong
            }

            // --- FALLBACK: The Vault (Sub-collections) ---
            // Only accessed if CRDT is missing or corrupted and root document doesn't contain sections/tracks.
            val subCollectionSong = try {
                val sectionsSnap = firestore.collection("songs").document(songId).collection("sections").get()
                val linesSnap = firestore.collection("songs").document(songId).collection("lines").get()

                val allLines = linesSnap.documents.mapNotNull { doc ->
                    try { doc.data<Line>().copy(id = doc.id) } catch (e: Exception) { null }
                }

                val sections = sectionsSnap.documents.mapNotNull { doc ->
                    try {
                        val s = doc.data<Section>().copy(id = doc.id)
                        val lines = allLines.filter { it.sectionId == s.id }.sortedBy { it.position }
                        s.copy(lines = lines)
                    } catch (e: Exception) { null }
                }.sortedBy { it.position }

                baseSong.copy(sections = if (sections.isNotEmpty()) sections else baseSong.sections)
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Vault fetch failed for $songId: $e")
                baseSong
            }

            AppLogger.d("SongRepository", "Returning Vault/JSON version for $songId (Sections: ${subCollectionSong.sections.size})")
            return subCollectionSong
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Firestore fetch failed for $songId: $e")
            return null
        }
    }

    suspend fun getSongById(id: String): Song? {
        val userId = auth.currentUser?.uid ?: ""
        // 1. Memory Check (Collaborations)
        _sharedSongs.value.find { it.id == id }?.let { return it }        

        // 2. Local Database Check
        val songEntity = database.songQueriesQueries.getSongById(id, userId).awaitAsOneOrNull()
        val localSong = if (songEntity != null) {
            try { json.decodeFromString<Song>(songEntity.data_) } catch (e: Exception) { null }
        } else null

        // 3. Firestore Check (Deep Hydration)
        val cloudSong = fetchFullSongFromFirestore(id)
        
        if (cloudSong != null) {
            // --- PRO POLICY: Binary-First Trust ---
            // If cloud has binary state, we assume it's the authoritative "Rich" version.
            // We hydrate local DB if local is missing, older, or is a shell.
            val localIsShell = localSong == null || localSong.sections.isEmpty()
            val shouldHydrate = localSong == null || 
                                cloudSong.lastModified > localSong.lastModified || 
                                (localIsShell && cloudSong.sections.isNotEmpty())

            if (shouldHydrate) {
                AppLogger.d("SongRepository", "Binary-First hydration for $id. (Cloud is ${if (localSong == null) "New" else if (cloudSong.lastModified > localSong.lastModified) "Newer" else "Richer"})")
                saveSong(cloudSong.copy(lastSynced = cloudSong.lastModified))
                return cloudSong
            }
            
            // If local is newer (offline edits), we return local but cloud might have merged metadata.
            if (localSong != null && localSong.lastModified > cloudSong.lastModified) {
                return localSong
            }
            return cloudSong
        }
        
        return localSong
    }

    suspend fun deleteSong(id: String) {
        val sharedSong = _sharedSongs.value.find { it.id == id }
        val userId = auth.currentUser?.uid
        if (sharedSong != null && userId != null) {
            // PRO SYNC: Collaborators should use 'leaveSong' to permanently remove their UID from the cloud.
            // Using saveSongToAllSources would fail because non-owners cannot edit metadata.
            try {
                if (isJs) {
                    val token = try { null } catch (e: Exception) { null }
                    val jsData = "{\"songId\":\"$id\"}"
                    songwritersnotebook.app.callIfJs("leaveSong", Any(), jsData, token)
                } else {
                    callCloudFunction("leaveSong", mapOf("songId" to id))
                }
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Error leaving shared song $id: $e")
            }

            // Cleanup local files for the shared song before removing it from memory
            sharedSong.recordings.values.flatten().forEach { recording ->
                try {
                    val localPath = getLocalFileForRecording(recording.id)
                    val file = fileSystem.getFile(localPath)
                    if (file.exists()) file.delete()
                } catch (e: Exception) { }
            }

            _sharedSongs.value = _sharedSongs.value.filter { it.id != id }
            database.songQueriesQueries.deleteSong(id)
            database.songQueriesQueries.deleteAllVersionsForSong(id, userId)
            crdtSyncManager.clearAllDeltas(id)
            return
        }
        val song = getSongById(id)
        song?.recordings?.values?.flatten()?.forEach { recording ->
            try {
                val localPath = getLocalFileForRecording(recording.id)
                val file = fileSystem.getFile(localPath)
                if (file.exists()) file.delete()
            } catch (e: Exception) { }
        }

        // PRO SYNC: Instead of physical deletion, we mark as deleted.
        // This prevents the background sync from re-downloading it from the cloud.
        if (song != null) {
            val tombstone = song.copy(
                isDeleted = true,
                lastModified = Clock.System.now(),
                collaborators = if (song.ownerId == userId) emptyList() else song.collaborators,
                viewers = if (song.ownerId == userId) emptyList() else song.viewers
            )
            // 1. Mark locally
            saveSong(tombstone)
            // 2. Mark in cloud if owner
            if (song.ownerId == userId) {
                AppLogger.d("SongRepository", "Triggering cloud deletion sync for '${song.title}' (${song.id})")
                saveSongToAllSources(tombstone, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, isUndoable = false)
            } else {
                AppLogger.d("SongRepository", "Skipping cloud deletion sync for '${song.title}' - current user $userId is not owner ${song.ownerId}")
            }
        }

        crdtSyncManager.clearAllDeltas(id)
    }
    private fun getLocalSongFlow(songId: String): Flow<Song?> {
        val userId = auth.currentUser?.uid ?: ""
        return database.songQueriesQueries.getSongById(songId, userId)
            .asFlow()
            .map { it.awaitAsOneOrNull() }
            .map { entity ->
                if (entity == null) return@map null
                try { json.decodeFromString<Song>(entity.data_) } catch (e: Exception) { null }
            }
    }

    fun observeSong(songId: String): Flow<Song?> {
        observedSongId = songId
        // Special case for tutorial song (local only)
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) {
            return getLocalSongFlow(songId).onCompletion { observedSongId = null }
        }

        return channelFlow {
            AppLogger.d("SongRepository", "observeSong starting for $songId")
            
            val userId = auth.currentUser?.uid ?: ""

            // 1. Instant Local Load (Offline-first / Fast-boot)
            val localEntity = database.songQueriesQueries.getSongById(songId, userId).awaitAsOneOrNull()
            val initialLocalSong = localEntity?.let {
                try { json.decodeFromString<Song>(it.data_) } catch (e: Exception) { null }
            }
            
            if (initialLocalSong != null) {
                AppLogger.d("SongRepository", "Instantly booting CRDT engine from Local DB for $songId.")
                val localCrdtData = decodeCrdtState(initialLocalSong.crdtState)
                crdtSyncManager.startSync(songId, localCrdtData, initialLocalSong)
            }

            var lastObservedEpoch = initialLocalSong?.crdtEpoch ?: 0

            // 2. Background Cloud Sync (CRDT Merging)
            // This runs asynchronously so it doesn't block the UI from loading the local song.
            repositoryScope.launch {
                val cloudSong = fetchFullSongFromFirestore(songId)
                if (cloudSong != null) {
                    AppLogger.d("SongRepository", "Background cloud fetch completed for $songId. Pushing to CRDT for causal merge.")
                    val cloudCrdtData = decodeCrdtState(cloudSong.crdtState)
                    // If startSync is called again for the same song, it invokes crdtDoc.merge() instead of load(),
                    // perfectly resolving the causal history between local and cloud without needing a majority rule!
                    crdtSyncManager.startSync(songId, cloudCrdtData, cloudSong, forceReboot = false)
                    
                    // PENDING SYNC RECOVERY: If our local song has unsaved offline edits,
                    // we must push our local state to the cloud now that we are (potentially) online.
                    if (initialLocalSong != null) {
                        val localModified = initialLocalSong.lastModified.toEpochMilliseconds()
                        val localSynced = initialLocalSong.lastSynced?.toEpochMilliseconds() ?: 0L
                        if (localModified > localSynced) {
                            AppLogger.d("SongRepository", "Recovering offline edits. Local is newer than lastSynced. Triggering background push.")
                            // Short delay to ensure CRDT engine is fully settled
                            kotlinx.coroutines.delay(1000)
                            
                            // Reconstruct the song to capture the fully merged CRDT state
                            val currentMergedSong = crdtSyncManager.getCurrentSong()
                            if (currentMergedSong != null) {
                                saveSongToAllSourcesNonBlocking(
                                    song = currentMergedSong,
                                    scope = songwritersnotebook.app.crdt.UndoScope.EDITOR,
                                    isUndoable = false
                                )
                            } else {
                                // Fallback
                                saveSongToAllSourcesNonBlocking(
                                    song = initialLocalSong,
                                    scope = songwritersnotebook.app.crdt.UndoScope.EDITOR,
                                    isUndoable = false
                                )
                            }
                        }
                    }
                }
            }
            
            val cloudDoc = firestore.collection("songs").document(songId)
        
            // 2. Cloud Flow: Watches the main document for binary CRDT state updates & metadata changes
            val cloudFlow: Flow<Song?> = cloudDoc.snapshots
                .map { snapshot ->
                AppLogger.d("FIRESTORE_USAGE", "READ: cloudDoc.snapshots fired for songId $songId")
                if (snapshot != null && snapshot.exists) {
                    val songShell = try { 
                        snapshot.data<Song>().copy(id = songId) 
                    } catch (e: Exception) {
                        AppLogger.d("SongRepository", "Failed to deserialize flow snapshot $songId: $e")
                        Song(
                            id = songId,
                            title = try { snapshot.get<String>("title") } catch(e2: Exception) { "" } ?: "",
                            ownerId = try { snapshot.get<String>("ownerId") } catch(e2: Exception) { "" } ?: "",
                            collaborators = try { snapshot.get<List<String>>("collaborators") } catch(e2: Exception) { emptyList() },
                            viewers = try { snapshot.get<List<String>>("viewers") } catch(e2: Exception) { emptyList() },
                            collaboratorEmails = try { snapshot.get<Map<String, String>>("collaboratorEmails") } catch(e2: Exception) { emptyMap() },
                            isDeleted = try { snapshot.get<Boolean>("isDeleted") } catch(e2: Exception) { false },
                            crdtEpoch = try { snapshot.get<Int>("crdtEpoch") } catch(e2: Exception) { 0 },
                            crdtState = try { snapshot.get<String>("crdtState") } catch(e2: Exception) { null }
                        )
                    }
                    
                    val newCrdtState = decodeCrdtState(songShell.crdtState)
                    val currentEpoch = songShell.crdtEpoch
                    val isEpochJump = currentEpoch > lastObservedEpoch
                    
                    if (newCrdtState != null) {
                        if (isEpochJump) {
                            AppLogger.d("SongRepository", "CRDT Epoch jumped ($lastObservedEpoch -> $currentEpoch). Forcing CRDT sync reboot.")
                            lastObservedEpoch = currentEpoch
                            crdtSyncManager.startSync(songId, newCrdtState, songShell, forceReboot = true)
                        } else {
                            crdtSyncManager.startSync(songId, newCrdtState, songShell, forceReboot = false)
                        }
                    } else if (crdtSyncManager.activeSongId != songId) {
                        val fullSong = fetchFullSongFromFirestore(songId)
                        if (fullSong != null) {
                            crdtSyncManager.startSync(songId, null, fullSong)
                            repositoryScope.launch {
                                saveSong(fullSong.copy(lastSynced = fullSong.lastModified))
                            }
                            return@map fullSong
                        }
                    }

                    repositoryScope.launch {
                        try {
                            kotlinx.coroutines.delay(300)
                            val currentMerged = crdtSyncManager.getCurrentSong()
                            val userId = auth.currentUser?.uid ?: ""
                            val localInDb = database.songQueriesQueries.getSongById(songId, userId).awaitAsOneOrNull()
                            val localSong = localInDb?.let {
                                try { json.decodeFromString<Song>(it.data_) } catch (e: Exception) { null }
                            }
                            val localModified = localSong?.lastModified?.toEpochMilliseconds() ?: 0L
                            val localSynced = localSong?.lastSynced?.toEpochMilliseconds() ?: 0L
                            
                            val hasUnsavedLocalEdits = crdtSyncManager.pendingDeltasCount.value > 0 || (localModified > localSynced)
                            
                            if (currentMerged != null && currentMerged.id == songId) {
                                val toSave = currentMerged.copy(
                                    lastSynced = songShell.lastModified,
                                    revisionId = songShell.revisionId ?: currentMerged.revisionId
                                )
                                val finalToSave = if (!hasUnsavedLocalEdits) {
                                    toSave.copy(lastModified = songShell.lastModified)
                                } else {
                                    toSave
                                }
                                saveSong(finalToSave)
                            } else {
                                saveSong(songShell.copy(lastSynced = songShell.lastModified))
                            }
                        } catch (e: Exception) {
                            AppLogger.d("SongRepository", "Error syncing remote snapshot to SQLite for $songId: $e")
                        }
                    }
                    
                    songShell as Song?
                } else (Song(id = "DOES_NOT_EXIST_SENTINEL", title = "", ownerId = "") as Song?)
            }.catch { e ->
                  AppLogger.d("SongRepository", "Firestore error for $songId: $e")
                  val msg = e.message?.lowercase() ?: ""
                  if (msg.contains("permission_denied") || msg.contains("permission denied")) {
                      emit(Song(id = "PERMISSION_DENIED_SENTINEL", title = "", ownerId = "") as Song?)
                  } else {
                      emit(Song(id = "DOES_NOT_EXIST_SENTINEL", title = "", ownerId = "") as Song?) 
                  }
              }.onStart { emit(null) }

            // 3. Local Flow: Fallback for Free/Hobbyist or Offline
            val localFlow = getLocalSongFlow(songId).onStart { emit(initialLocalSong) }

            // 4. Combine Cloud, Local, CRDT, and Presence
            val combinedFlow: Flow<Song?> = combine(
                cloudFlow, 
                localFlow, 
                crdtSyncManager.songFlow.onStart { emit(null) }, 
                crdtSyncManager.presenceFlow.onStart { emit(emptyMap()) }
            ) { cloud, local, crdt, presence ->
                AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] COMBINE: cloudId=${cloud?.id}, localId=${local?.id}, localIsDeleted=${local?.isDeleted}, crdtId=${crdt?.id}, crdtIsDeleted=${crdt?.isDeleted}")

                if (cloud?.id == "PERMISSION_DENIED_SENTINEL") {
                    AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] Access revoked (Permission Denied). Flow terminated.")
                    throw Exception("Permission denied")
                }
                
                if (cloud?.id == "DOES_NOT_EXIST_SENTINEL") {
                    if (local == null) {
                        AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] Cloud missing and Local null. Returning null.")
                        return@combine null
                    } else {
                        // Song exists locally but not in cloud. Prioritize local.
                    }
                }
                
                val finalCloud = if (cloud?.id == "DOES_NOT_EXIST_SENTINEL") null else cloud

                val base = finalCloud ?: local ?: return@combine null

                val finalPresence = presence

                if (crdt != null && crdt.id == songId) {
                    AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] Using CRDT state. baseIsDeleted=${base.isDeleted}")
                    return@combine crdt.copy(
                        id = base.id,
                        title = crdt.title.ifBlank { base.title },
                        ownerId = base.ownerId,
                        ownerUsername = base.ownerUsername,
                        collaborators = base.collaborators,
                        viewers = base.viewers,
                        isPublic = base.isPublic,
                        isDeleted = base.isDeleted,
                        collaboratorEmails = base.collaboratorEmails,
                        pendingCollaborators = base.pendingCollaborators,
                        harmonyChatHistory = base.harmonyChatHistory,
                        presence = finalPresence,
                        lastSynced = base.lastSynced,
                        sizeInBytes = base.sizeInBytes,
                        isSyncError = base.isSyncError,
                        revisionId = if (!crdt.revisionId.isNullOrEmpty()) crdt.revisionId else base.revisionId,
                        crdtState = null 
                    ) as Song?
                }

                val cloudIsShell = finalCloud != null && finalCloud.sections.isEmpty() && (finalCloud.studioState?.tracks?.isEmpty() ?: true)
                val localIsNewer = finalCloud != null && local != null && local.lastModified > finalCloud.lastModified
                val shouldPrioritizeLocal = local != null && (finalCloud == null || cloudIsShell || localIsNewer)
                
                val fallbackBase = if (shouldPrioritizeLocal) {
                    local!!.copy(
                        presence = finalPresence,
                        collaborators = finalCloud?.collaborators ?: local.collaborators,
                        viewers = finalCloud?.viewers ?: local.viewers,
                        ownerId = finalCloud?.ownerId ?: local.ownerId,
                        isPublic = finalCloud?.isPublic ?: local.isPublic
                    )
                } else {
                    (finalCloud ?: local!!).copy(presence = finalPresence)
                }
                
                AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] Fallback logic used. shouldPrioritizeLocal=$shouldPrioritizeLocal, finalIsDeleted=${fallbackBase.isDeleted}")
                fallbackBase as Song?
            }.filterNotNull().map { it as Song? }.catch { e ->
                AppLogger.d("SongRepository", "DEBUG_FLOW [$songId] Error in combined song flow: $e")
                emit(null)
            }
            
            combinedFlow.collect { send(it) }
        }.onCompletion { observedSongId = null }
    }

    fun saveSongToAllSourcesNonBlocking(
        song: Song,
        scope: songwritersnotebook.app.crdt.UndoScope,
        isUndoable: Boolean = true,
        sectionIdToUpdate: String? = null,
        trackIdToUpdate: String? = null,
        deletedLineIds: List<String>? = null,
        deletedSectionIds: List<String>? = null,
        deletedTrackIds: List<String>? = null,
        deletedTrackItemIds: List<String>? = null,
        onComplete: (SaveSongResponse?) -> Unit = {}
    ) {
        repositoryScope.launch {
            val result = saveSongToAllSources(
                song = song,
                scope = scope,
                isUndoable = isUndoable,
                sectionIdToUpdate = sectionIdToUpdate,
                trackIdToUpdate = trackIdToUpdate,
                deletedLineIds = deletedLineIds,
                deletedSectionIds = deletedSectionIds,
                deletedTrackIds = deletedTrackIds,
                deletedTrackItemIds = deletedTrackItemIds
            )
            try { withContext(Dispatchers.Main) { onComplete(result) } } catch (e: Exception) {}
        }
    }

    fun saveSongNonBlocking(song: Song, scope: songwritersnotebook.app.crdt.UndoScope, isUndoable: Boolean = true, onComplete: (SaveSongResponse?) -> Unit = {}) {
        saveSongToAllSourcesNonBlocking(song = song, scope = scope, isUndoable = isUndoable, onComplete = onComplete)
    }

    suspend fun saveSongToAllSources(
        song: Song,
        scope: songwritersnotebook.app.crdt.UndoScope,
        isUndoable: Boolean = true,
        sectionIdToUpdate: String? = null,
        trackIdToUpdate: String? = null,
        deletedLineIds: List<String>? = null,
        deletedSectionIds: List<String>? = null,
        deletedTrackIds: List<String>? = null,
        deletedTrackItemIds: List<String>? = null
    ): SaveSongResponse? {
        val activeSongId = crdtSyncManager.activeSongId
        AppLogger.d("SongRepository", "saveSongToAllSources for '${song.title}' (${song.id}). isDeleted: ${song.isDeleted}, activeSongId: $activeSongId")
        
        // PRO SYNC: If we are saving a song that is NOT the active one (e.g. from Home Screen or Background Sync),
        // we use its existing crdtState instead of the sync manager's live engine.
        val (currentCrdtData, deltaKeys) = if (activeSongId == song.id) {
            AppLogger.d("SongRepository", "Using active CRDT engine for '${song.id}'")
            crdtSyncManager.getCurrentSnapshotWithKeys()
        } else {
            AppLogger.d("SongRepository", "Using stored crdtState for inactive song '${song.id}'")
            val bytes = decodeCrdtState(song.crdtState)
            bytes to emptySet<String>()
        }

        // PRO SYNC GUARD: If the song is deleted locally, only allow the save if it is a deletion request (isDeleted = true).
        val userId = auth.currentUser?.uid ?: ""
        val songInDb = database.songQueriesQueries.getSongById(song.id, userId).awaitAsOneOrNull()

        if (songInDb?.isDeleted == 1L && !song.isDeleted) {
            AppLogger.d("SongRepository", "Blocking save for deleted song '${song.title}' to prevent resurrection.")
            return SaveSongResponse("success", "Song is deleted")
        }

        if (song.id == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) {
            saveSong(song)
            return null
        }
        val cleanSong = song.copy(lastModified = Instant.fromEpochMilliseconds(song.lastModified.toEpochMilliseconds()))
        val songWithOwner = if (cleanSong.ownerId.isEmpty() && userId.isNotEmpty()) cleanSong.copy(ownerId = userId) else cleanSong
        
        val base64Crdt = currentCrdtData?.let { encodeCrdtState(it) }
        val songWithCrdt = songWithOwner.copy(crdtState = base64Crdt ?: songWithOwner.crdtState)
        
        val request = SaveSongRequest(
            songId = songWithCrdt.id,
            revisionId = songWithCrdt.revisionId,
            lastModified = songWithCrdt.lastModified.toEpochMilliseconds(),
            song = if (sectionIdToUpdate == null && trackIdToUpdate == null) songWithCrdt else null,
            section = if (sectionIdToUpdate != null) songWithCrdt.sections.find { it.id == sectionIdToUpdate } else null,
            track = if (trackIdToUpdate != null) songWithCrdt.studioState?.tracks?.find { it.id == trackIdToUpdate } else null,
            deletedLineIds = deletedLineIds,
            deletedSectionIds = deletedSectionIds,
            deletedTrackIds = deletedTrackIds,
            deletedTrackItemIds = deletedTrackItemIds,
            isDeleted = if (songWithCrdt.isDeleted) true else null
        )
        AppLogger.d("SongRepository", "Created SaveSongRequest for '${song.id}'. isDeleted: ${request.isDeleted}")
        return performSaveRequest(songWithCrdt, request, crdtState = base64Crdt, deltaKeys = deltaKeys, isUndoable = isUndoable)
    }

    private suspend fun performSaveRequest(
        song: Song, 
        request: SaveSongRequest, 
        crdtState: String? = null,
        deltaKeys: Set<String>? = null,
        isUndoable: Boolean = true
    ): SaveSongResponse? {
        val sanitizedSong = song.sanitize()
        saveSong(sanitizedSong)

        if (crdtSyncManager.activeSongId == song.id) {
            return try {
                val response = if (isJs) {
                    val user = auth.currentUser
                    if (user != null) {
                        val token = user.getIdToken(false)
                        val jsonString = kotlinx.serialization.json.Json.encodeToString(songwritersnotebook.app.data.SaveSongRequest.serializer(), request)
                        songwritersnotebook.app.callIfJs("saveSong", Any(), jsonString, token)
                        songwritersnotebook.app.data.SaveSongResponse(status = "success", message = "Parsed manually", revisionId = null, totalBytes = null)
                    } else null
                } else {
                    callCloudFunction("saveSong", request).data<SaveSongResponse>()
                }
                
                if (response?.status == "success") {
                    deltaKeys?.let { crdtSyncManager.clearDeltas(song.id, it) }
                    val syncedSong = sanitizedSong.copy(
                        lastSynced = sanitizedSong.lastModified,
                        isSyncError = false,
                        revisionId = response.revisionId ?: sanitizedSong.revisionId
                    )
                    saveSong(syncedSong)
                }
                response
            } catch (e: Exception) {
                handleSaveError(song, e)
            }
        }

        // Queue for background sync
        triggerSync()
        
        return SaveSongResponse("success", "Cloud sync pending")
    }

    private val _syncNotifications = MutableSharedFlow<String>(replay = 0)
    val syncNotifications = _syncNotifications.asSharedFlow()

    val canEditorUndo: StateFlow<Boolean> = crdtSyncManager.canEditorUndo
    val canEditorRedo: StateFlow<Boolean> = crdtSyncManager.canEditorRedo
    val canStudioUndo: StateFlow<Boolean> = crdtSyncManager.canStudioUndo
    val canStudioRedo: StateFlow<Boolean> = crdtSyncManager.canStudioRedo

    suspend fun undo(currentSong: Song, scope: songwritersnotebook.app.crdt.UndoScope) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        crdtSyncManager.undo(currentSong, userId, scope)
    }

    suspend fun redo(currentSong: Song, scope: songwritersnotebook.app.crdt.UndoScope) {
        val userId = auth.currentUser?.uid ?: "anonymous"
        crdtSyncManager.redo(currentSong, userId, scope)
    }

    private suspend fun handleSaveError(song: Song, e: Exception): SaveSongResponse {
        val userId = auth.currentUser?.uid
        val msg = e.message ?: ""
        
        // Handle explicit subscription errors
        if (msg.contains("Cloud storage requires a Studio subscription")) {
            return SaveSongResponse(if (songwritersnotebook.app.getPlatform().name.contains("Web")) "Studio Subscription Required" else "error", msg)
        }

        // Handle Revision/Permission conflicts via Merge
        if (msg.contains("permission-denied") || msg.contains("PERMISSION_DENIED") || msg.contains("Revision mismatch")) {
            try {
                val doc = firestore.collection("songs").document(song.id).get()
                if (doc.exists) {
                    val remoteSong = doc.data<Song>()
                    val remoteBytes = decodeCrdtState(remoteSong.crdtState)
                    val localBytes = decodeCrdtState(song.crdtState)
                    
                    // Recover title from remote if local is missing it
                    val bestTitle = if (song.title.isNotBlank()) song.title else remoteSong.title

                    if (remoteBytes != null && localBytes != null) {
                        val mergeDoc = songwritersnotebook.app.crdt.SongCrdtDoc()
                        mergeDoc.load(remoteBytes)
                        mergeDoc.merge(localBytes)
                        
                        val mergedCrdt = encodeCrdtState(mergeDoc.save())
                        val recoveredSong = mergeDoc.getSong().copy(
                            id = song.id, 
                            title = bestTitle,
                            ownerId = remoteSong.ownerId, 
                            ownerUsername = remoteSong.ownerUsername, 
                            collaborators = remoteSong.collaborators, 
                            viewers = remoteSong.viewers, 
                            collaboratorEmails = remoteSong.collaboratorEmails, 
                            revisionId = remoteSong.revisionId, 
                            lastModified = Clock.System.now(), 
                            crdtState = mergedCrdt, 
                            recordings = song.recordings,
                            isSyncError = false // SUCCESSFUL MERGE: CLEAR ERROR
                        )
                        saveSong(recoveredSong)
                        // Emit notification for ViewModels to show a Toast
                        _syncNotifications.emit("Cloud changes merged.")
                        // Don't flag as error if we successfully merged
                        return SaveSongResponse("success", "Sync conflict merged. Your work was preserved.")
                    } else {
                        saveSong(remoteSong.copy(isSyncError = false, title = if (remoteSong.title.isNotBlank()) remoteSong.title else song.title))
                    }
                }
            } catch (ignore: Exception) { 
                AppLogger.d("SongRepository", "Recovery failed: $ignore")
            }
        }

        // HEALING: Even if userId is null (offline session edge case), we MUST save locally to prevent data loss.
        // We only skip if the error is "not found" (meaning the song was deleted from the server).
        val isTransient = msg.contains("timeout", ignoreCase = true) || msg.contains("network", ignoreCase = true) || msg.contains("unavailable", ignoreCase = true) || msg.contains("internal", ignoreCase = true) || msg.contains("deadline", ignoreCase = true)
        if (!msg.contains("not found") && !isTransient) {
            saveSong(song.copy(isSyncError = true))
        }
        
        return SaveSongResponse("error", msg)
    }

    suspend fun refreshSongFromCloud(songId: String) {
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        try {
            val doc = firestore.collection("songs").document(songId).get()
            if (doc.exists) {
                try {
                    val song = doc.data<Song>()
                    saveSong(song.copy(lastSynced = song.lastModified, isSyncError = false))
                } catch (e: Exception) {
                    println("DEBUG_SYNC: Failed to deserialize song $songId from cloud in refresh: $e")
                }
            }
        } catch (e: Exception) { }
    }

    suspend fun syncSongsWithFirestore(onComplete: () -> Unit) {
        val userId = auth.currentUser?.uid ?: return onComplete()
        
        // OPTIMIZATION: Metadata Sync (Fast)
        // We fetch the "list" of songs in parallel to unblock the Home Screen ASAP.
        withContext(songwritersnotebook.app.AppDispatchers.IO) {
            var retryCount = 0
            val maxRetries = 3
            var success = false
            
            while (retryCount < maxRetries && !success) {
                try {
                    coroutineScope {
                        val ownedDeferred = async { firestore.collection("songs").where { "ownerId" equalTo userId }.get() }
                        val sharedDeferred = async { firestore.collection("songs").where { "collaborators" contains userId }.get() }
                        val viewerDeferred = async { firestore.collection("songs").where { "viewers" contains userId }.get() }

                        val ownedSnapshot = ownedDeferred.await()
                        val sharedSnapshot = sharedDeferred.await()
                        val viewerSnapshot = viewerDeferred.await()

                        val ownedSongsList = ownedSnapshot.documents.mapNotNull { 
                            try { it.data<Song>().copy(id = it.id) } catch (e: Exception) { 
                                println("DEBUG_SYNC: Failed to deserialize owned song ${it.id}: $e")
                                var crdtDocData: String? = null
                                try {
                                    val crdtDoc = firestore.collection("songs").document(it.id).collection("crdt").document("payload").get()
                                    if (crdtDoc.exists) crdtDocData = crdtDoc.get<String>("data")
                                } catch(e3: Exception) { }

                                Song(
                                    id = it.id,
                                    title = try { it.get<String>("title") } catch(e2: Exception) { "" } ?: "",
                                    ownerId = try { it.get<String>("ownerId") } catch(e2: Exception) { "" } ?: "",
                                    collaborators = try { it.get<List<String>>("collaborators") } catch(e2: Exception) { emptyList() },
                                    viewers = try { it.get<List<String>>("viewers") } catch(e2: Exception) { emptyList() },
                                    collaboratorEmails = try { it.get<Map<String, String>>("collaboratorEmails") } catch(e2: Exception) { emptyMap() },
                                    isDeleted = try { it.get<Boolean>("isDeleted") } catch(e2: Exception) { false },
                                    crdtState = crdtDocData
                                )
                            } 
                        }
                        println("DEBUG_SYNC: successfully downloaded ${ownedSongsList.size} owned cloud songs")
                        val sharedSongsList = (sharedSnapshot.documents + viewerSnapshot.documents)
                            .mapNotNull { 
                                try { 
                                    val s = it.data<Song>().copy(id = it.id)
                                    if (s.isDeleted) null else s
                                } catch (e: Exception) { 
                                    println("DEBUG_SYNC: Failed to deserialize shared song ${it.id}: $e")
                                    var crdtDocData: String? = null
                                    try {
                                        val crdtDoc = firestore.collection("songs").document(it.id).collection("crdt").document("payload").get()
                                        if (crdtDoc.exists) crdtDocData = crdtDoc.get<String>("data")
                                    } catch(e3: Exception) { }

                                    val fallback = Song(
                                        id = it.id,
                                        title = try { it.get<String>("title") } catch(e2: Exception) { "" } ?: "",
                                        ownerId = try { it.get<String>("ownerId") } catch(e2: Exception) { "" } ?: "",
                                        collaborators = try { it.get<List<String>>("collaborators") } catch(e2: Exception) { emptyList() },
                                        viewers = try { it.get<List<String>>("viewers") } catch(e2: Exception) { emptyList() },
                                        collaboratorEmails = try { it.get<Map<String, String>>("collaboratorEmails") } catch(e2: Exception) { emptyMap() },
                                        isDeleted = try { it.get<Boolean>("isDeleted") } catch(e2: Exception) { false },
                                        crdtState = crdtDocData
                                    )
                                    if (fallback.isDeleted) null else fallback
                                } 
                            }
                            .distinctBy { it.id }
                            .filter { it.ownerId != userId && !it.isDeleted && (it.collaborators.contains(userId) || it.viewers.contains(userId)) }
                            .map { it.copy(lastSynced = it.lastModified, isSyncError = false) }

                        // Immediately update the songs in memory so they appear in the UI
                        val currentSharedMap = _sharedSongs.value.associateBy { it.id }
                        val cloudSharedMap = sharedSongsList.associateBy { it.id }
                        _sharedSongs.value = (currentSharedMap.keys + cloudSharedMap.keys).map { id ->
                            val memorySong = currentSharedMap[id]
                            val cloudSong = cloudSharedMap[id]
                            if (cloudSong == null) memorySong!!
                            else if (memorySong == null) cloudSong
                            else if (memorySong.lastModified > cloudSong.lastModified) memorySong else cloudSong 
                        }

                        val currentOwnedMap = _songs.value.associateBy { it.id }
                        val cloudOwnedMap = ownedSongsList.associateBy { it.id }
                        _songs.value = (currentOwnedMap.keys + cloudOwnedMap.keys).map { id ->
                            val memorySong = currentOwnedMap[id]
                            val cloudSong = cloudOwnedMap[id]
                            if (cloudSong == null) memorySong!!
                            else if (memorySong == null) cloudSong
                            else if (memorySong.lastModified > cloudSong.lastModified) memorySong else cloudSong
                        }

                        success = true // Mark as successful to break out of retry loop
                        
                        // We will pass ownedSongsList and sharedSongsList down by saving them in variables outside the loop, 
                        // but actually we can just move the rest of the logic into this try block!
                        
                        // UNBLOCK UI: The Home Screen can now show the list (titles/dates).
                        // Any deeper content merges will happen in the background.
                        withContext(Dispatchers.Main) { onComplete() }

                        // HEAVY SYNC (Background): Compare local/cloud and merge content.
                        repositoryScope.launch {
                            val localSongs = database.songQueriesQueries.getAllSongsInternal(userId).awaitAsList().mapNotNull { entity ->
                                try { 
                                    val s = json.decodeFromString<Song>(entity.data_)
                                    s.copy(isDeleted = entity.isDeleted == 1L)
                                } catch (e: Exception) { null } 
                            }
                            val localMap = localSongs.associateBy { it.id }
                            val cloudMap = (ownedSongsList + sharedSongsList).associateBy { it.id }
                            
                            for (id in (cloudMap.keys + localMap.keys)) {
                                val cloud = cloudMap[id]
                                val local = localMap[id]

                                // 1. Handle Deletions (Tombstones)
                                if (cloud?.isDeleted == true || local?.isDeleted == true) { 
                                    val finalSong = (cloud ?: local)!!.copy(isDeleted = true)
                                    if (local?.isDeleted != true || local.title != finalSong.title) {
                                        saveSong(finalSong)
                                    }
                                    if (local?.isDeleted == true && cloud?.isDeleted != true && local.ownerId == userId) {
                                        saveSongToAllSources(local, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, isUndoable = false)
                                    }
                                    continue 
                                }

                                if (cloud != null && local != null) {
                                    // --- PRO POLICY: Binary-First Merge ---
                                    if (local.crdtState != null && cloud.crdtState != null && local.crdtState != cloud.crdtState) {
                                        try {
                                            val mergeDoc = songwritersnotebook.app.crdt.SongCrdtDoc()
                                            val cloudBytes = decodeCrdtState(cloud.crdtState)
                                            val localBytes = decodeCrdtState(local.crdtState)
                                            
                                            if (cloudBytes != null) mergeDoc.load(cloudBytes)
                                            if (localBytes != null) mergeDoc.merge(localBytes)
                                            
                                            val mergedSong = mergeDoc.getSong().copy(
                                                id = id, 
                                                title = if (local.lastModified > cloud.lastModified) local.title else cloud.title,
                                                ownerId = cloud.ownerId, 
                                                ownerUsername = cloud.ownerUsername, 
                                                collaborators = cloud.collaborators,
                                                viewers = cloud.viewers,
                                                collaboratorEmails = cloud.collaboratorEmails,
                                                isPublic = cloud.isPublic,
                                                studioState = cloud.studioState,
                                                revisionId = cloud.revisionId,
                                                isDeleted = false,
                                                lastSynced = Clock.System.now(),
                                                isSyncError = false,
                                                crdtState = songwritersnotebook.app.utils.Utils.byteArrayToBase64(mergeDoc.save())
                                            )
                                            saveSong(mergedSong)
                                            if (local.ownerId == userId) {
                                                saveSongToAllSources(mergedSong, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, isUndoable = false)
                                            }
                                        } catch (e: Exception) {
                                            val adopted = if (local.lastModified > cloud.lastModified) local else cloud
                                            saveSong(adopted.copy(lastSynced = adopted.lastModified, isSyncError = false))
                                        }
                                    } else {
                                        val adopted = if (local.lastModified > cloud.lastModified) local else cloud
                                        val finalToSave = if (adopted == cloud && cloud.crdtState != null && cloud.sections.isEmpty()) {
                                            val cloudBytes = decodeCrdtState(cloud.crdtState)
                                            if (cloudBytes != null) parseSongFromCrdt(cloudBytes, cloud) ?: cloud else cloud
                                        } else adopted
                                        saveSong(finalToSave.copy(lastSynced = finalToSave.lastModified, isSyncError = false))
                                    }
                                } else if (cloud != null) {
                                    val hydratedCloud = if (cloud.crdtState != null && cloud.sections.isEmpty()) {
                                        val cloudBytes = decodeCrdtState(cloud.crdtState)
                                        if (cloudBytes != null) parseSongFromCrdt(cloudBytes, cloud) ?: cloud else cloud
                                    } else cloud
                                    saveSong(hydratedCloud.copy(lastSynced = hydratedCloud.lastModified, isSyncError = false))
                                } else if (local != null) {
                                    if (local.ownerId.isNotEmpty() && local.ownerId != "anonymous" && local.ownerId != userId) {
                                        AppLogger.d("SongRepository", "Access revoked for shared song ${local.id}. Deleting local copy.")
                                        deleteSong(local.id)
                                    } else {
                                        val tier = _currentUserProfile.value?.subscriptionTier ?: "FREE"
                                        if (local.ownerId == userId && tier == "STUDIO" && !local.isDeleted) {
                                            saveSongToAllSources(local, scope = songwritersnotebook.app.crdt.UndoScope.EDITOR, isUndoable = false)
                                        }
                                    }
                                }
                            }
                            
                            // Check for deleted local songs that are now missing from remote
                            localMap.values.forEach { localSong ->
                                if (localSong.ownerId == userId && !localSong.isDeleted && !cloudMap.containsKey(localSong.id)) {
                                    // It was hard-deleted remotely (e.g. cloud function)
                                    saveSong(localSong.copy(isDeleted = true))
                                }
                            }
                            
                            fetchInvitations()
                        }
                    }
                } catch (e: Exception) {
                    AppLogger.d("SongRepository", "Error in syncSongsWithFirestore (Attempt ${retryCount + 1}): $e")
                    retryCount++
                    if (retryCount < maxRetries) {
                        kotlinx.coroutines.delay(1000L * retryCount) // Exponential-ish backoff: 1s, 2s
                    } else {
                        withContext(Dispatchers.Main) { onComplete() }
                    }
                }
            }
        }
    }
    
    suspend fun fetchInvitations() {
        try {
            AppLogger.d("SongRepository", "[INVITE_DEBUG] Calling fetchInvitations Cloud Function")
            val result = callCloudFunction("fetchInvitations", mapOf<String, String>())
            val data = result.data<songwritersnotebook.app.data.FetchInvitationsResponse>()
            AppLogger.d("SongRepository", "[INVITE_DEBUG] Received ${data.invitations.size} invitations from Cloud Function")
            _invitations.value = data.invitations.distinctBy { it.songId }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "[INVITE_DEBUG] Error fetching invitations via Cloud Function: $e")
        }
    }
    
    suspend fun respondToInvitation(invitation: Invitation, accept: Boolean) {
        try {
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"invitationId\":\"${invitation.id}\",\"accept\":$accept}"
                songwritersnotebook.app.callIfJs("respondToInvitation", Any(), jsData, token)
            } else {
                callCloudFunction("respondToInvitation", mapOf("invitationId" to invitation.id, "accept" to accept))
            }
            
            // 1. Remove from invitations UI immediately
            _invitations.value = _invitations.value.filter { it.id != invitation.id }
            
            if (accept) {
                // 2. OPTIMISTIC INJECTION: Construct the song shell immediately so it appears in the UI
                try {
                    val userId = auth.currentUser?.uid
                    val isEditor = invitation.role == "editor"
                    
                    val acceptedSong = Song(
                        id = invitation.songId,
                        title = invitation.songTitle,
                        ownerId = invitation.ownerId,
                        collaborators = if (isEditor && userId != null) listOf(userId) else emptyList(),
                        viewers = if (!isEditor && userId != null) listOf(userId) else emptyList(),
                        lastModified = kotlinx.datetime.Clock.System.now()
                    )
                    
                    val songJson = json.encodeToString(acceptedSong.copy(presence = emptyMap(), isDirty = false))
                    kotlinx.coroutines.withContext(songwritersnotebook.app.AppDispatchers.IO) {
                        database.songQueriesQueries.insertSong(
                            id = acceptedSong.id, 
                            ownerId = acceptedSong.ownerId, 
                            title = acceptedSong.title, 
                            lastModified = acceptedSong.lastModified.toEpochMilliseconds(), 
                            bpm = acceptedSong.bpm.toLong(), 
                            isDeleted = if (acceptedSong.isDeleted) 1L else 0L, 
                            data_ = songJson
                        )
                    }
                    
                    val currentShared = _sharedSongs.value.toMutableList()
                    if (!currentShared.any { it.id == acceptedSong.id }) {
                        currentShared.add(acceptedSong)
                        _sharedSongs.value = currentShared
                    }
                } catch (e: Exception) {
                    AppLogger.d("SongRepository", "Failed to optimistically inject accepted song: $e")
                }
                
                // 3. (Removed immediate syncSongsWithFirestore to prevent index propagation race condition where the song is deleted locally)
            }
        } catch (e: Exception) { 
            throw e 
        }
    }

    suspend fun sendInvitation(songId: String, email: String, role: String) {
        val localSong = getSongById(songId)
        if (localSong != null) {
            val updatedPending = localSong.pendingCollaborators.toMutableMap()
            updatedPending[email] = role
            saveSong(localSong.copy(pendingCollaborators = updatedPending))
        }

        try {
            val request = songwritersnotebook.app.data.SendInvitationRequest(songId = songId, email = email, role = role)
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"songId\":\"$songId\",\"email\":\"$email\",\"role\":\"$role\"}"
                songwritersnotebook.app.callIfJs("sendInvitation", Any(), jsData, token)
            } else {
                callCloudFunction("sendInvitation", request)
            }
            val updated = firestore.collection("songs").document(songId).get()
            if (updated.exists) { val s = updated.data<Song>(); saveSong(s.copy(lastSynced = s.lastModified, isSyncError = false)) }
        } catch (e: Exception) { 
            if (localSong != null) saveSong(localSong)
            throw e 
        }
    }

    suspend fun cancelInvitation(songId: String, email: String) {
        val localSong = getSongById(songId)
        if (localSong != null) {
            val updatedPending = localSong.pendingCollaborators.toMutableMap()
            updatedPending.remove(email)
            saveSong(localSong.copy(pendingCollaborators = updatedPending))
        }

        try { 
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"songId\":\"$songId\",\"email\":\"$email\"}"
                songwritersnotebook.app.callIfJs("cancelInvitation", Any(), jsData, token)
            } else {
                callCloudFunction("cancelInvitation", mapOf("songId" to songId, "email" to email))
            }
        } catch (e: Exception) { 
            if (localSong != null) saveSong(localSong)
            throw e 
        }
    }

    fun switchUser(context: Any?, userId: String?) {
        if (userId == null) { _currentUserProfile.value = null; _sharedSongs.value = emptyList(); _invitations.value = emptyList() }
    }
    
    suspend fun hasAnonymousSongs(): Boolean = withContext(songwritersnotebook.app.AppDispatchers.IO) {
        val userId = auth.currentUser?.uid ?: ""
        (database.songQueriesQueries.getAllSongs().awaitAsList() as List<SongEntity>).any { try { val s = json.decodeFromString<Song>(it.data_); s.ownerId.isEmpty() || s.ownerId == "anonymous" } catch (e: Exception) { false } }
    }
    
    suspend fun mergeAnonymousSongs(newUserId: String) {
        val userId = auth.currentUser?.uid ?: ""
        (database.songQueriesQueries.getAllSongs().awaitAsList() as List<SongEntity>).forEach { try { val s = json.decodeFromString<Song>(it.data_); if (s.ownerId.isEmpty() || s.ownerId == "anonymous") saveSong(s.copy(ownerId = newUserId)) } catch (e: Exception) {} }
    }

    suspend fun deleteAnonymousSongs() {
        val userId = auth.currentUser?.uid ?: ""
        (database.songQueriesQueries.getAllSongs().awaitAsList() as List<SongEntity>).forEach { try { val s = json.decodeFromString<Song>(it.data_); if (s.ownerId.isEmpty() || s.ownerId == "anonymous") deleteSong(it.id) } catch (e: Exception) {} }
    }

    suspend fun recalculateAndSetStorageUsage(userId: String) {
        try { val res = callCloudFunctionNoArg("recalculateUserStorage"); AppLogger.d("SongRepository", "Storage recalculated.") } catch (e: Exception) {}
    }

    fun getLocalFileForRecording(recordingId: String, isCache: Boolean = false): String {
        if (appFilesDir.path.isNotEmpty()) {
             val dir = if (isCache) {
                 val cachePath = dependencyProvider?.getCacheDir()
                 if (cachePath != null) fileSystem.getFile(cachePath) else fileSystem.getAppFilesDirectory()
             } else fileSystem.getAppFilesDirectory()
             
             val subDirName = if (isCache) "audio_cache" else "recordings"
             val recordingsDir = fileSystem.getFile(dir, subDirName)
             if (!recordingsDir.exists()) recordingsDir.mkdirs()
             return fileSystem.getFile(recordingsDir, "$recordingId.m4a").absolutePath
        }
        return "${dependencyProvider?.getCacheDir() ?: ""}/$recordingId.m4a"
    }

    fun getResolvedRecordingUrl(recordingId: String, remoteUrl: String): String {
        val path = getLocalFileForRecording(recordingId)
        if (fileSystem.getFile(path).exists()) return path
        
        val cachePath = getLocalFileForRecording(recordingId, isCache = true)
        if (fileSystem.getFile(cachePath).exists()) return cachePath
        
        // If the "remote" URL is actually an absolute local path from another device/install, 
        // and the file doesn't exist at our current local path, then we don't have a valid source.
        if (remoteUrl.startsWith("/") || remoteUrl.contains("data/user") || remoteUrl.contains("Users/")) {
            return ""
        }
        
        return remoteUrl
    }

    private suspend fun downloadRecording(url: String, songId: String, targetId: String, recordingId: String, ownerId: String): Boolean {
        var finalUrl = url
        
        // If we have a local path instead of a URL, we need to resolve the real URL from the cloud
        if (finalUrl.isBlank() || finalUrl.startsWith("/") || finalUrl.contains("data/user") || finalUrl.contains("Users/")) {
            AppLogger.d("SongRepository", "Resolving remote URL for recording $recordingId with local-only path.")
            try {
                // Match the path structure used in uploadAndFinalizeRecording
                // Path: users/$owner/songs/$songId/recordings/$tempId.$ext
                val ext = if (url.lowercase().endsWith(".webm")) "webm" else "m4a"
                val storagePath = "users/$ownerId/songs/$songId/recordings/$recordingId.$ext"
                finalUrl = Firebase.storage.reference.child(storagePath).getDownloadUrl()
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Failed to resolve storage URL for $recordingId: $e")
                return false
            }
        }

        if (finalUrl.isBlank() || !finalUrl.startsWith("http")) return false
        
        val file = fileSystem.getFile(getLocalFileForRecording(recordingId))
        return try {
            val bytes = if (songwritersnotebook.app.getPlatform().name.contains("Web", true)) songwritersnotebook.app.utils.NetworkUtils().fetchAuthenticated(finalUrl)
            else { val c = dependencyProvider?.getHttpClient() ?: return false; val r = c.get(finalUrl); if (r.status.value in 200..299) r.readBytes() else return false }
            file.writeBytes(bytes); true
        } catch (e: Exception) { 
            AppLogger.d("SongRepository", "Download failed for $recordingId: $e")
            false 
        }
    }

    fun syncRecordingsForSong(song: Song, onProgress: (String) -> Unit) {
        repositoryScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            song.recordings.forEach { (targetId, list) -> 
                list.forEach { rec -> 
                    val localPath = getLocalFileForRecording(rec.id)
                    val fileExists = fileSystem.getFile(localPath).exists()
                    
                    if (!fileExists && rec.isSynced) {
                        withContext(Dispatchers.Main) { onProgress("Downloading ${rec.label}...") }
                        downloadRecording(rec.filePath, song.id, targetId, rec.id, song.ownerId)
                    }
                } 
            }
            withContext(Dispatchers.Main) { onProgress("") }
        }
    }

    fun uploadMissingRecordings() {
        val userId = auth.currentUser?.uid ?: return
        repositoryScope.launch { _songs.value.filter { it.ownerId == userId }.forEach { song -> song.recordings.forEach { (target, list) -> list.forEach { rec -> if (!rec.isSynced && !rec.filePath.startsWith("http")) uploadRecordingNonBlocking(rec.filePath, song.id, target, rec.id, rec.label, rec.id) } } } }
    }

    fun uploadRecordingNonBlocking(path: String, songId: String, targetId: String, tempId: String, label: String, replaceId: String? = null) {
        repositoryScope.launch {
            _uploadStatus.update { it + (tempId to UploadStatus.UPLOADING) }
            var success = false
            var attempt = 0
            val maxAttempts = 3
            while (attempt < maxAttempts && !success) {
                attempt++
                try {
                    if (uploadAndFinalizeRecording(path, songId, targetId, tempId, label, replaceId) != null) {
                        success = true
                    } else if (attempt < maxAttempts) {
                        val backoff = (2.0).pow(attempt.toDouble()).toLong() * 1000
                        kotlinx.coroutines.delay(backoff)
                    }
                } catch (e: Exception) {
                    if (attempt < maxAttempts) {
                        val backoff = (2.0).pow(attempt.toDouble()).toLong() * 1000
                        kotlinx.coroutines.delay(backoff)
                    }
                }
            }
            if (success) {
                _uploadStatus.update { it + (tempId to UploadStatus.SUCCESS) }
                kotlinx.coroutines.delay(3000)
                _uploadStatus.update { it - tempId }
            } else {
                _uploadStatus.update { it + (tempId to UploadStatus.ERROR) }
                kotlinx.coroutines.delay(5000)
                _uploadStatus.update { it - tempId }
            }
        }
    }

    suspend fun uploadAndFinalizeRecording(path: String, songId: String, targetId: String, tempId: String, label: String, replaceId: String? = null): RecordingMetadata? {
        val userId = auth.currentUser?.uid ?: return null
        val song = getSongById(songId) ?: return null
        val owner = if (song.ownerId.isNotEmpty()) song.ownerId else userId
        val ref = storage.reference.child("users/$owner/songs/$songId/recordings/$tempId.m4a")
        return try {
            val file = fileSystem.getFile(path); if (!file.exists()) return null
            ref.putData(file.readBytes().toFirebaseData())
            val url = ref.getDownloadUrl()
            
            // PRO STORAGE: Move uploaded file to cache to reclaim primary storage space.
            try {
                val cachePath = getLocalFileForRecording(tempId, isCache = true)
                val cacheFile = fileSystem.getFile(cachePath)
                file.copyTo(cacheFile, overwrite = true)
                file.delete()
                AppLogger.d("SongRepository", "Promoted $tempId to cache.")
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Failed to promote $tempId to cache: $e")
            }

            val meta = RecordingMetadata(id = tempId, filePath = url, label = label, timestamp = Clock.System.now().toEpochMilliseconds(), isSynced = true)
            val recs = song.recordings.toMutableMap(); recs[targetId] = (recs[targetId] ?: emptyList()).filter { it.id != replaceId && it.id != tempId } + meta
            var studio = song.studioState; if (studio != null) studio = studio.copy(tracks = studio.tracks.map { t -> t.copy(items = t.items.map { i -> if (i.data.id == replaceId || i.data.id == tempId) i.copy(data = i.data.copy(id = meta.id, url = meta.filePath, name = meta.label)) else i }) })
            saveSongToAllSources(song.copy(recordings = recs, studioState = studio, lastModified = Clock.System.now()), scope = songwritersnotebook.app.crdt.UndoScope.STUDIO, isUndoable = false); meta
        } catch (e: Exception) { e.printStackTrace(); null }
    }

    /**
     * PRO STORAGE: Periodically purges cached audio files older than 7 days.
     */
    fun cleanupOldCache() {
        repositoryScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            try {
                val now = Clock.System.now().toEpochMilliseconds()
                val weekInMillis = 7 * 24 * 60 * 60 * 1000L
                val cacheDir = fileSystem.getCacheDirectory()
                val files = cacheDir.listFiles()
                files?.forEach { file: songwritersnotebook.platform.PlatformFile ->
                    if (file.name.endsWith(".m4a") && (now - file.lastModified() > weekInMillis)) {
                        file.delete()
                        AppLogger.d("SongRepository", "Purged old cache file: ${file.name}")
                    }
                }
            } catch (e: Exception) {
                AppLogger.d("SongRepository", "Cache cleanup failed: $e")
            }
        }
    }

    suspend fun findUserByEmail(email: String): Pair<String, String>? {
        return try { val res = callCloudFunction("findUserByEmail", FindUserRequest(email)); val d = res.data<FindUserResponse>(); Pair(d.uid, d.email) } catch (e: Exception) { null }
    }

    fun clearAllLocalAudio(onComplete: (Int) -> Unit) {
        repositoryScope.launch(songwritersnotebook.app.AppDispatchers.IO) { var count = 0; (_songs.value + _sharedSongs.value).forEach { s -> s.recordings.values.flatten().forEach { r -> if (r.isSynced) try { val f = fileSystem.getFile(getLocalFileForRecording(r.id)); if (f.exists()) { f.delete(); count++ } } catch (e: Exception) {} } }; withContext(Dispatchers.Main) { onComplete(count) } }
    }

    suspend fun submitFeedback(msg: String) { firestore.collection("feedback").add(buildJsonObject { put("userId", JsonPrimitive(auth.currentUser?.uid ?: "anonymous")); put("message", JsonPrimitive(msg)); put("platform", JsonPrimitive("KMP")); put("timestamp", JsonPrimitive(Clock.System.now().toString())) }) }

    suspend fun getVersions(songId: String): Flow<List<SongVersion>> {
        val userId = auth.currentUser?.uid; val song = getSongById(songId); val isCloud = (song?.ownerId == userId && getCurrentUserProfile()?.subscriptionTier == "STUDIO") || (song?.ownerId?.isNotEmpty() == true && song.ownerId != userId)
        return if (isCloud) kotlinx.coroutines.flow.flow { try { val snapshot = firestore.collection("songs").document(songId).collection("versions").orderBy("timestamp", dev.gitlive.firebase.firestore.Direction.DESCENDING).get(); AppLogger.d("FIRESTORE_USAGE", "READ: versions.get fired for songId $songId"); emit(snapshot.documents.mapNotNull { d -> try { d.data<SongVersion>() } catch (e: Exception) { AppLogger.d("SongRepository", "Failed to parse SongVersion ${d.id}: $e"); null } }) } catch(e: Exception) { AppLogger.d("SongRepository", "Failed to get versions: $e"); emit(emptyList()) } }
        else database.songQueriesQueries.getVersionsForSong(songId, userId ?: "").asFlow().mapToList(Dispatchers.Default).map { it.mapNotNull { e -> try { SongVersion(id = e.id, songId = e.songId, timestamp = e.timestamp, versionNumber = e.versionNumber.toInt(), type = try { VersionType.valueOf(e.type) } catch (ex: Exception) { VersionType.CHECKPOINT }, notes = e.notes, songData = json.decodeFromString(e.data_)) } catch (ex: Exception) { AppLogger.d("SongRepository", "Failed to parse SongVersion ${e.id}: $ex"); null } } }.catch { emit(emptyList()) }
    }

    suspend fun saveVersion(song: Song, type: VersionType, notes: String, versionId: String? = null) {
        val userId = auth.currentUser?.uid
        val profile = getCurrentUserProfile()
        val tier = profile?.subscriptionTier ?: "FREE"
        val isStudio = tier == "STUDIO"
        val isOwner = song.ownerId == userId
        
        // --- PRO QUOTA ENFORCEMENT ---
        val used = profile?.dataStorageUsedBytes ?: 0.0
        val quota = profile?.dataStorageQuotaBytes ?: 0.0 // 0 bytes default
        if (isOwner && used >= quota) {
            AppLogger.d("SongRepository", "Version save aborted - Quota exceeded ($used / $quota)")
            return
        }

        if (isOwner && !isStudio && tier != "HOBBYIST" && tier != "LIFETIME") return
        if (!isOwner && type != VersionType.CHECKPOINT && type != VersionType.MANUAL) return

        // Capture full CRDT binary state for accurate history
        val latestBinary = if (song.id == crdtSyncManager.activeSongId) getCurrentCrdtState() else null
        val songToSnap = if (latestBinary != null) {
            song.copy(crdtState = songwritersnotebook.app.utils.Utils.byteArrayToBase64(latestBinary))
        } else song

        val useCloud = if (isOwner) isStudio && song.ownerId.isNotEmpty() else true
        val vid = versionId ?: com.benasher44.uuid.uuid4().toString()
        val ts = Clock.System.now().toEpochMilliseconds()

        try {
            if (useCloud) {
                val ref = firestore.collection("songs").document(song.id).collection("versions")
                val snap = ref.orderBy("timestamp").get()
                val vers = snap.documents.mapNotNull { try { it.data<SongVersion>() } catch (e: Exception) { AppLogger.d("SongRepository", "Prune check failed to parse ${it.id}: $e"); null } }
                val existing = vers.find { it.id == vid }
                if (existing == null && vers.size >= 50) {
                    (vers.firstOrNull { it.type == VersionType.CHECKPOINT } ?: vers.firstOrNull())?.let { ref.document(it.id).delete() }
                }
                val num = existing?.versionNumber ?: ((vers.maxOfOrNull { it.versionNumber } ?: 0) + 1)
                ref.document(vid).set(SongVersion(id = vid, songId = song.id, timestamp = ts, versionNumber = num, type = type, notes = notes, songData = songToSnap))
            } else {
                withContext(songwritersnotebook.app.AppDispatchers.IO) {
                    val currentVers = database.songQueriesQueries.getVersionsForSong(song.id, userId ?: "").awaitAsList()
                    val existing = currentVers.find { it.id == vid }
                    val nextNum = existing?.versionNumber?.toLong() ?: ((currentVers.maxOfOrNull { it.versionNumber } ?: 0) + 1).toLong()
                    
                    if (existing != null) {
                        database.songQueriesQueries.updateVersionNotes(notes, vid, userId ?: "")
                        // Assuming we want to update the data as well, but SQLite implementation uses insert/replace
                        database.songQueriesQueries.insertVersion(id = vid, ownerId = userId ?: "", songId = song.id, 
                            timestamp = ts.toLong(), 
                            versionNumber = nextNum, 
                            type = type.name, 
                            notes = notes, 
                            data_ = json.encodeToString(songToSnap)
                        )
                    } else {
                        database.songQueriesQueries.insertVersion(id = vid, ownerId = userId ?: "", songId = song.id, 
                            timestamp = ts.toLong(), 
                            versionNumber = nextNum, 
                            type = type.name, 
                            notes = notes, 
                            data_ = json.encodeToString(songToSnap)
                        )
                    }
                    
                    // Cleanup old checkpoints (keep last 10)
                    database.songQueriesQueries.deleteOldVersions(song.id, userId ?: "", song.id, userId ?: "", 10L)
                }
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Error saving version: $e")
        }
    }

    fun saveVersionNonBlocking(song: Song, type: VersionType, notes: String, versionId: String? = null) {
        repositoryScope.launch { saveVersion(song, type, notes, versionId) }
    }

    suspend fun updateVersionNotes(songId: String, versionId: String, notes: String) {
        val song = getSongById(songId)
        val userId = auth.currentUser?.uid
        val profile = getCurrentUserProfile()
        val isCloud = (song?.ownerId == userId && profile?.subscriptionTier == "STUDIO") || (song?.ownerId?.isNotEmpty() == true && song.ownerId != userId)
        if (isCloud) {
            firestore.collection("songs").document(songId).collection("versions").document(versionId).update("notes" to notes)
        } else {
            this.database.songQueriesQueries.updateVersionNotes(notes, versionId, userId ?: "")
        }
    }

    suspend fun deleteVersion(songId: String, versionId: String) {
        val song = getSongById(songId)
        val userId = auth.currentUser?.uid
        val isCloud = (song?.ownerId == userId && getCurrentUserProfile()?.subscriptionTier == "STUDIO") || (song?.ownerId?.isNotEmpty() == true && song.ownerId != userId)
        if (isCloud) {
            firestore.collection("songs").document(songId).collection("versions").document(versionId).delete()
        } else {
            database.songQueriesQueries.deleteVersion(versionId, userId ?: "")
        }
    }

    suspend fun removeAllCollaborationsForUser(uid: String) {
        try { 
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"uid\":\"$uid\"}"
                songwritersnotebook.app.callIfJs("removeAllCollaborationsForUser", Any(), jsData, token)
            } else {
                callCloudFunction("removeAllCollaborationsForUser", mapOf("uid" to uid))
            }
        } catch (e: Exception) {}
    }

    suspend fun leaveAllCollaborationsFromUser(uid: String) {
        try { 
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"uid\":\"$uid\"}"
                songwritersnotebook.app.callIfJs("leaveAllCollaborationsFromUser", Any(), jsData, token)
            } else {
                callCloudFunction("leaveAllCollaborationsFromUser", mapOf("uid" to uid))
            }
        } catch (e: Exception) {}
    }

    fun syncAllRecordings(onProgress: (String) -> Unit) {
        repositoryScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
            _songs.value.forEach { song ->
                song.recordings.forEach { (targetId, list) ->
                    list.forEach { rec ->
                        if (!fileSystem.getFile(getLocalFileForRecording(rec.id)).exists()) {
                            withContext(Dispatchers.Main) { onProgress("Downloading ${rec.label}...") }
                            downloadRecording(rec.filePath, song.id, targetId, rec.id, song.ownerId)
                        }
                    }
                }
            }
            withContext(Dispatchers.Main) { onProgress("") }
        }
    }

    suspend fun deleteRecordingFromFirebase(songId: String, recordingId: String) {
        val userId = auth.currentUser?.uid ?: return
        val song = getSongById(songId) ?: return
        val owner = if (song.ownerId.isNotEmpty()) song.ownerId else userId
        try {
            val ref = storage.reference.child("users/$owner/songs/$songId/recordings/$recordingId.m4a")
            ref.delete()
        } catch (e: Exception) {}
    }

    suspend fun recoverOrphanedRecordings(songId: String): Int {
        val userId = auth.currentUser?.uid ?: return 0
        val song = getSongById(songId) ?: return 0
        val owner = if (song.ownerId.isNotEmpty()) song.ownerId else userId
        
        var recoveredCount = 0
        try {
            val ref = storage.reference.child("users/$owner/songs/$songId/recordings")
            val result = ref.listAll()
            
            val allKnownIds = song.recordings.values.flatten().map { it.id }.toSet()
            
            val updatedRecordings = song.recordings.toMutableMap()
            val unassignedList = updatedRecordings["unassigned"]?.toMutableList() ?: mutableListOf()
            
            for (item in result.items) {
                val filename = item.name
                val recordingId = filename.substringBeforeLast(".")
                
                if (!allKnownIds.contains(recordingId)) {
                    val url = item.getDownloadUrl()
                    val meta = songwritersnotebook.app.data.RecordingMetadata(
                        id = recordingId,
                        filePath = url,
                        label = "Recovered Audio",
                        timestamp = kotlinx.datetime.Clock.System.now().toEpochMilliseconds(),
                        isSynced = true
                    )
                    unassignedList.add(meta)
                    recoveredCount++
                }
            }
            
            if (recoveredCount > 0) {
                updatedRecordings["unassigned"] = unassignedList
                val updatedSong = song.copy(
                    recordings = updatedRecordings, 
                    lastModified = kotlinx.datetime.Clock.System.now()
                )
                crdtSyncManager.pushUpdate(
                    song = updatedSong,
                    songId = songId,
                    userId = userId,
                    scope = songwritersnotebook.app.crdt.UndoScope.EDITOR
                )
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to recover orphans: ${e.message}")
        }
        return recoveredCount
    }

    private val activeObservers = mutableSetOf<String>()

    fun registerObserver(observerId: String) {
        activeObservers.add(observerId)
    }

    /**
     * PRO SELF-HEALING: Performs a deep reset of a song's local state.
     * Use this to resolve rare CRDT corruption or sync deadlocks.
     */
    suspend fun repairSong(songId: String): Boolean = withContext(AppDispatchers.IO) {
        val userId = auth.currentUser?.uid ?: return@withContext false
        try {
            songwritersnotebook.app.utils.AppLogger.i("SongRepository", "Initiating Deep Repair for song $songId")
            
            // 1. Stop active sync to clear memory cache
            crdtSyncManager.stopSync(songId, userId)
            
            // 2. Clear local SQLite entry
            database.songQueriesQueries.deleteSong(songId)
            
            // 3. Clear all local version history for this song
            database.songQueriesQueries.deleteAllVersionsForSong(songId, userId)
            
            // 4. Force fresh cloud fetch (Full Hydration)
            val cloudSong = fetchFullSongFromFirestore(songId)
            if (cloudSong != null) {
                // Save fresh copy to local DB
                saveSong(cloudSong.copy(lastSynced = cloudSong.lastModified))
                songwritersnotebook.app.utils.AppLogger.i("SongRepository", "Deep Repair successful for $songId")
                true
            } else {
                songwritersnotebook.app.utils.AppLogger.w("SongRepository", "Deep Repair failed: Song $songId not found in cloud.")
                false
            }
        } catch (e: Exception) {
            songwritersnotebook.app.utils.AppLogger.e("SongRepository", "Error during Deep Repair of $songId", e)
            false
        }
    }

    fun stopSongObservation(songId: String? = null, userId: String? = null, observerId: String) {
        activeObservers.remove(observerId)
        repositoryScope.launch {
            kotlinx.coroutines.delay(500)
            if (activeObservers.isEmpty()) {
                crdtSyncManager.stopSync(songId, userId)
            } else {
                AppLogger.d("SongRepository", "Skipping stopSongObservation for $songId because active observers remain: $activeObservers")
            }
        }
    }

    fun saveSongToLocalNonBlocking(song: Song) {
        repositoryScope.launch {
            saveSong(song)
        }
    }

    fun finalSaveAndStop(
        song: Song,
        scope: songwritersnotebook.app.crdt.UndoScope,
        userId: String?,
        observerId: String,
        deletedLineIds: List<String> = emptyList(),
        deletedSectionIds: List<String> = emptyList(),
        deletedTrackIds: List<String> = emptyList(),
        deletedTrackItemIds: List<String> = emptyList(),
        sessionVersionId: String? = null
    ) {
        // PRO FIX: Perform an immediate local save of the metadata (Title, etc) 
        // using the survivor scope so the Home Screen sees it instantly.
        val songToSaveLocal = if (song.ownerId.isNotEmpty()) {
            // Optimistically update lastSynced so the Home Screen immediately shows "Synced"
            // The Master Baker will handle the actual cloud commit.
            song.copy(lastSynced = song.lastModified, isSyncError = false)
        } else {
            song
        }
        saveSongToLocalNonBlocking(songToSaveLocal)

        repositoryScope.launch {
            // 1. Force the CRDT update to finish embedding state
            pushCrdtUpdateSuspend(song.id, song, scope, shouldPushToCloud = true)
            
            // 2. We SKIP the full SQLite/Cloud save (saveSongToAllSources) here to avoid write amplification
            // and HTTP timeout errors on Web. The Master Baker handles the Cloud save via RTDB deltas!

            // 3. Save the final version history snapshot
            saveVersion(songToSaveLocal, VersionType.CHECKPOINT, "End of session", sessionVersionId)
            
            // 4. ONLY THEN stop observation and clear the CRDT engine
            stopSongObservation(song.id, userId, observerId)
        }
    }
    suspend fun getCurrentCrdtState(): ByteArray? = crdtSyncManager.getCurrentState()
    fun clearRemotePresence(songId: String, userId: String, deviceId: String) = crdtSyncManager.clearRemotePresence(songId, userId, deviceId)

    suspend fun registerSession(id: String, name: String, plat: String) { 
        try { 
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"deviceId\":\"$id\",\"deviceName\":\"$name\",\"platform\":\"$plat\"}"
                songwritersnotebook.app.callIfJs("registerSession", Any(), jsData, token)
            } else {
                callCloudFunction("registerSession", RegisterSessionRequest(id, name, plat))
            }
        } catch (e: Exception) {} 
    }
    suspend fun removeSession(id: String) { 
        try { 
            if (isJs) {
                val token = try { null } catch (e: Exception) { null }
                val jsData = "{\"deviceId\":\"$id\"}"
                songwritersnotebook.app.callIfJs("removeSession", Any(), jsData, token)
            } else {
                callCloudFunction("removeSession", RemoveSessionRequest(id))
            }
        } catch (e: Exception) {} 
    }

    /**
     * Decodes a base64 CRDT state string, automatically decompressing if it's GZIPPED.
     */
    private fun decodeCrdtState(base64: String?): ByteArray? {
        if (base64 == null) return null
        return try {
            val bytes = songwritersnotebook.app.utils.Utils.base64ToByteArray(base64)
            if (songwritersnotebook.app.utils.GzipUtils.isGzipped(bytes)) {
                songwritersnotebook.app.utils.GzipUtils.ungzip(bytes)
            } else {
                bytes
            }
        } catch (e: Exception) {
            println("SongRepository: Error decoding/decompressing CRDT state: ${e.stackTraceToString()}")
            null
        }
    }

    /**
     * Encodes a ByteArray to Base64, compressing with GZIP if larger than 50KB.
     */
    private fun encodeCrdtState(data: ByteArray): String {
        return try {
            // Compress if > 50KB. GZIP has overhead, so don't bother for tiny docs.
            val finalData = if (data.size > 50000) {
                songwritersnotebook.app.utils.GzipUtils.gzip(data)
            } else {
                data
            }
            songwritersnotebook.app.utils.Utils.byteArrayToBase64(finalData)
        } catch (e: Exception) {
            println("SongRepository: Error encoding/compressing CRDT state: $e")
            songwritersnotebook.app.utils.Utils.byteArrayToBase64(data)
        }
    }

    fun parseSongFromCrdt(crdtState: ByteArray, baseSong: Song): Song? {
        println("DEBUG_CRDT_TRACE: parseSongFromCrdt start")
        return try {
            println("DEBUG_CRDT_TRACE: creating tempDoc")
            val tempDoc = songwritersnotebook.app.crdt.SongCrdtDoc()
            println("DEBUG_CRDT_TRACE: calling load")
            tempDoc.load(crdtState)
            println("DEBUG_CRDT_TRACE: calling getSong")
            val reconstructedSong = tempDoc.getSong()
            println("DEBUG_CRDT_TRACE: calling copy")
            val copied = reconstructedSong.copy(
                id = baseSong.id,
                ownerId = baseSong.ownerId,
                ownerUsername = baseSong.ownerUsername,
                collaborators = baseSong.collaborators,
                viewers = baseSong.viewers,
                isPublic = baseSong.isPublic,
                collaboratorEmails = baseSong.collaboratorEmails,
                pendingCollaborators = baseSong.pendingCollaborators,
                presence = baseSong.presence,
                revisionId = baseSong.revisionId,
                lastSynced = baseSong.lastSynced,
                isSyncError = baseSong.isSyncError
            )
            println("DEBUG_CRDT_TRACE: copy successful")
            copied
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Error parsing old CRDT state for undo: ${e.stackTraceToString()}")
            null
        }
    }

    // --- Harmony Chat History ---

    suspend fun getHarmonyChatHistory(songId: String, userId: String, limit: Long): List<songwritersnotebook.app.data.HarmonyChatMessage> {
        val messages = mutableListOf<songwritersnotebook.app.data.HarmonyChatMessage>()
        try {
            // Fetch local cache
            val localEntities = database.songQueriesQueries.getHarmonyChatHistory(songId, userId, limit).awaitAsList()
            
            if (localEntities.isNotEmpty()) {
                messages.addAll(localEntities.map { entity ->
                    songwritersnotebook.app.data.HarmonyChatMessage(
                        id = entity.id,
                        text = entity.text,
                        isUser = entity.isUser == 1L,
                        userId = entity.userId,
                        timestamp = entity.timestamp
                    )
                })

                // Sync from Firestore concurrently
                repositoryScope.launch {
                    syncHarmonyChatsFromRemote(songId, userId, limit)
                }
            } else {
                // If local cache is empty (e.g., fresh load on Web), await the remote fetch before returning
                syncHarmonyChatsFromRemote(songId, userId, limit)
                
                // Read from local again which should now be populated
                val newlySynced = database.songQueriesQueries.getHarmonyChatHistory(songId, userId, limit).awaitAsList()
                messages.addAll(newlySynced.map { entity ->
                    songwritersnotebook.app.data.HarmonyChatMessage(
                        id = entity.id,
                        text = entity.text,
                        isUser = entity.isUser == 1L,
                        userId = entity.userId,
                        timestamp = entity.timestamp
                    )
                })
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to fetch chat history: \${e.message}")
        }
        return messages
    }

    private suspend fun syncHarmonyChatsFromRemote(songId: String, userId: String, limit: Long) {
        try {
            println("DEBUG_REPO: syncHarmonyChatsFromRemote fetching for $userId")
            val query = Firebase.firestore.collection("songs").document(songId)
                .collection("harmony_chats")
                .where { "userId" equalTo userId }

            val snapshot = query.get()
            val documents = snapshot.documents
            println("DEBUG_REPO: syncHarmonyChatsFromRemote got \${documents.size} documents from Firestore")
            
            // Sort locally to avoid needing a composite index in Firestore
            val parsedMessages = documents.mapNotNull { doc ->
                try {
                    doc.data<songwritersnotebook.app.data.HarmonyChatMessage>()
                } catch (e: Exception) {
                    println("DEBUG_REPO: Failed to parse remote chat message: \${e.message}")
                    null
                }
            }.sortedByDescending { it.timestamp }.take(limit.toInt())
            
            parsedMessages.forEach { data ->
                try {
                    database.songQueriesQueries.insertHarmonyChatMessage(
                        id = data.id,
                        songId = songId,
                        userId = data.userId ?: userId,
                        timestamp = data.timestamp,
                        text = data.text,
                        isUser = if (data.isUser) 1L else 0L
                    )
                } catch (e: Exception) {
                    println("DEBUG_REPO: Failed to insert parsed chat message: \${e.message}")
                }
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to fetch remote chats: \${e.message}")
        }
    }

    suspend fun saveHarmonyChatMessage(songId: String, message: songwritersnotebook.app.data.HarmonyChatMessage) {
        try {
            // Save to local cache
            database.songQueriesQueries.insertHarmonyChatMessage(
                id = message.id,
                songId = songId,
                userId = message.userId ?: "",
                timestamp = message.timestamp,
                text = message.text,
                isUser = if (message.isUser) 1L else 0L
            )

            // Push to Firestore subcollection
            Firebase.firestore.collection("songs").document(songId)
                .collection("harmony_chats").document(message.id)
                .set(message, encodeDefaults = true)
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to save chat message: \${e.message}")
        }
    }

    suspend fun clearHarmonyChatHistory(songId: String, userId: String) {
        try {
            // Clear local
            database.songQueriesQueries.clearHarmonyChatHistory(songId, userId)

            // Note: Firebase client SDK doesn't support deleting a whole collection.
            // Ideally we'd invoke a cloud function, or query and delete in batches.
            // For now, we query and delete the recent ones as a best effort.
            repositoryScope.launch {
                try {
                    val docs = Firebase.firestore.collection("songs").document(songId)
                        .collection("harmony_chats")
                        .where { "userId" equalTo userId }
                        .get()

                    docs.documents.forEach { doc ->
                        doc.reference.delete()
                    }
                } catch (e: Exception) {
                    AppLogger.d("SongRepository", "Failed to delete remote chats: \${e.message}")
                }
            }
        } catch (e: Exception) {
            AppLogger.d("SongRepository", "Failed to clear chat history: \${e.message}")
        }
    }
}
